import React from 'react';
import {
  Box, Grid, Button, CircularProgress, Typography,
  Checkbox, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, IconButton,
  TextField, Alert, Chip, Paper, Stack, Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { postRequest, getRequest, deleteRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import _ from 'lodash';
import Form_Details, { ModulesConfig } from 'Containers/Billing/static/companySetup';
import DynamicForm from 'Components/DynamicForm';
import ServiceSelectionModal from 'Components/ServiceSelectionModal';
import IdCardServiceConfigModal from 'Components/IdCardServiceConfigModal';
import IdCardServiceConfigPanel from 'Components/IdCardServiceConfigPanel';
import BiometricServiceConfigPanel from 'Components/BiometricServiceConfigPanel';
import {
  parseIdCardConfigFromNotes,
  serializeIdCardConfigToNotes,
  calculateIdCardConfigTotal,
  validateIdCardConfig,
  normalizeIdCardConfig,
} from 'Containers/Billing/idCardServiceConfig';
import {
  parseBiometricConfigFromNotes,
  serializeBiometricConfigToNotes,
  calculateBiometricConfigTotal,
  validateBiometricConfig,
  normalizeBiometricConfig,
  createEmptyBiometricConfig,
  BIOMETRIC_KINDS,
} from 'Containers/Billing/biometricServiceConfig';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { parseBillingDateOnly } from 'Containers/Agreement/agreementFinance';

const DEFAULT_ID_CARD_GST_PERCENT = 18;

function CompanyOrder(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const config_id = urlParams.get('config_id');
  // const year = urlParams.get('year');
  const navigate = useNavigate();
  const [form_details] = React.useState(Form_Details[0]['page_details']['sub_sections']);
  const [loading, set_loading] = React.useState(config_id ? true : false);
  const [submitDisable, set_submitDisable] = React.useState(false);
  const [field_details, set_field_details] = React.useState(null);
  const [setup_details, set_setup_details] = React.useState({});
  const [fieldErrors, set_fieldErrors] = React.useState({});
  const [overallFetchedData, setoverallFetchedData] = React.useState({});


  // Dynamic services state
  const [otherServices, setOtherServices] = React.useState([]);
  const [serviceFieldDetails, setServiceFieldDetails] = React.useState({});
  const [serviceFieldErrors, setServiceFieldErrors] = React.useState({}); // { serviceId: { fieldName: errorMessage } }
  const [serviceModalOpen, setServiceModalOpen] = React.useState(false);
  const [idCardConfigModalOpen, setIdCardConfigModalOpen] = React.useState(false);
  const [pendingIdCardSelection, setPendingIdCardSelection] = React.useState(null);
  // Default (agreement) ID card lines: opt-in GST. Item-wise ID cards stay GST-free.
  const [idCardAddGst, setIdCardAddGst] = React.useState({});

  // Service payment terms state
  const [serviceTerms, setServiceTerms] = React.useState({}); // { serviceId: [{ id: 0 }, ...] }
  const [serviceTermDetails, setServiceTermDetails] = React.useState({}); // { serviceId: { termId: { term_receive_date, amount_percentage, amount_rs } } }
  const [serviceTermErrors, setServiceTermErrors] = React.useState({}); // { serviceId: { termId_field: error } }
  const [serviceTermValidationErrors, setServiceTermValidationErrors] = React.useState({}); // { serviceId: error }

  // Modules state
  const [selectedModules, setSelectedModules] = React.useState({});
  const [serviceExpanded, setServiceExpanded] = React.useState({});
  const expandedInitRef = React.useRef(false);

  const initialDataRef = React.useRef(null);
  const isDataLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (!loading && !isDataLoadedRef.current) {
      const timer = setTimeout(() => {
        initialDataRef.current = JSON.stringify({
          setup_details,
          otherServices,
          serviceFieldDetails,
          serviceTerms,
          serviceTermDetails,
          selectedModules
        });
        isDataLoadedRef.current = true;
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, setup_details, otherServices, serviceFieldDetails, serviceTerms, serviceTermDetails, selectedModules]);

  const pricing = React.useRef(null);
  const additional = React.useRef(null);
  const serviceRefs = React.useRef({});
  const removedServiceIds = React.useRef([]);
  const removedTermIds = React.useRef([]);

  /** Treat null / '' / 0 as empty so DB defaults (0.00) don't look like a filled amount. */
  const isTermAmountFilled = (val) => {
    if (val == null || val === '') return false;
    const n = parseFloat(val);
    return !Number.isNaN(n) && Math.abs(n) > 0.0001;
  };

  const resolveStudentStrength = (payload = {}) => {
    const report = Number(payload.client_report_student_count) || 0;
    const agreement = Number(payload.agreement_student_count) || 0;
    const saved = Number(payload.student_strength) || 0;
    if (report > 0) return report;
    if (agreement > 0) return agreement;
    return saved > 0 ? saved : '';
  };

  const getTermPercentageSum = (serviceId, termDetailsOverride) => {
    const list = serviceTerms[serviceId] || [];
    const details = termDetailsOverride || serviceTermDetails[serviceId] || {};
    return list.reduce((sum, term) => {
      return sum + (parseFloat(details[term.id]?.amount_percentage) || 0);
    }, 0);
  };

  const isIdCardCatalogService = React.useCallback((serviceObj = {}, planObj = {}) => {
    if (serviceObj?.code === 'ID_CARDS') return true;
    const sn = String(serviceObj?.name || '').toLowerCase().replace(/\s+/g, ' ');
    const pn = String(planObj?.name || '').toLowerCase();
    const bn = String(planObj?.billing_name || '').toLowerCase();
    const blob = `${sn} ${pn} ${bn}`;
    return (
      blob.includes('id card')
      || blob.includes('idcard')
    );
  }, []);

  const isIdCardGstEligibleLine = React.useCallback((serviceItem) => (
    Boolean(serviceItem)
    && (
      Boolean(serviceItem.idCardConfig)
      || isIdCardCatalogService(serviceItem.service, serviceItem.plan)
    )
  ), [isIdCardCatalogService]);

  const resolveDefaultIdCardGstPercent = React.useCallback((plan) => {
    const fromPlan = parseFloat(plan?.default_tax_amount);
    if (!Number.isNaN(fromPlan) && fromPlan > 0) return fromPlan;
    return DEFAULT_ID_CARD_GST_PERCENT;
  }, []);

  const syncIdCardPricingFromConfig = React.useCallback((serviceId, idCardConfig) => {
    const total = calculateIdCardConfigTotal(idCardConfig);
    set_setup_details((prev) => ({
      ...prev,
      [`service_amount_${serviceId}`]: total > 0 ? total.toFixed(2) : '',
      [`item_count_${serviceId}`]: 1,
    }));
  }, []);

  const isBiometricCatalogService = React.useCallback((serviceObj = {}, planObj = {}) => {
    const code = String(serviceObj?.code || '');
    if (code === BIOMETRIC_KINDS.auto.code || code === BIOMETRIC_KINDS.manual.code) return true;
    const sn = String(serviceObj?.name || '').toLowerCase();
    const pn = String(planObj?.name || '').toLowerCase();
    const bn = String(planObj?.billing_name || '').toLowerCase();
    return `${sn} ${pn} ${bn}`.includes('biometric');
  }, []);

  const syncBiometricPricingFromConfig = React.useCallback((serviceId, biometricConfig) => {
    const count = parseFloat(biometricConfig?.deviceCount) || 0;
    const fee = parseFloat(biometricConfig?.fee) || 0;
    set_setup_details((prev) => ({
      ...prev,
      [`item_count_${serviceId}`]: count > 0 ? count : '',
      [`service_amount_${serviceId}`]: fee >= 0 && biometricConfig?.fee !== '' ? String(fee) : '',
    }));
  }, []);

  const isDigitalMarketingService = React.useCallback((serviceObj = {}, planObj = {}) => {
    if (isIdCardCatalogService(serviceObj, planObj)) return false;
    if (isBiometricCatalogService(serviceObj, planObj)) return false;
    // Use ?? to treat 0 as a valid feature ID (not falsy)
    if (serviceObj?.digitalservice != null && serviceObj.digitalservice !== '' && serviceObj.digitalservice !== undefined) {
      return true;
    }
    const dm = planObj?.digital_marketing_features;
    if (Array.isArray(dm) && dm.length > 0) return true;
    const serviceName = String(serviceObj?.name || '').toLowerCase();
    const planName = String(planObj?.name || '').toLowerCase();
    const billingName = String(planObj?.billing_name || '').toLowerCase();
    const serviceType = String(planObj?.service_type || '').toLowerCase();
    return (
      serviceName.includes('digital marketing') ||
      planName.includes('digital') ||
      billingName.includes('digital') ||
      serviceType === 'digital_marketing'
    );
  }, [isIdCardCatalogService, isBiometricCatalogService]);

  const isErpServiceLine = React.useCallback((serviceItem) => {
    if (!serviceItem || serviceItem.idCardConfig || serviceItem.biometricConfig) return false;
    if (isIdCardCatalogService(serviceItem.service, serviceItem.plan)) return false;
    if (isBiometricCatalogService(serviceItem.service, serviceItem.plan)) return false;
    if (isDigitalMarketingService(serviceItem.service, serviceItem.plan)) return false;
    const notes = String(serviceItem.notes || '').toLowerCase();
    if (notes.includes('bulk erp order') || notes.includes('auto erp order')) return true;
    if (serviceItem.service?.agreement_erp_id != null && serviceItem.service.agreement_erp_id !== '') return true;
    const serviceName = String(serviceItem.service?.name || '').trim().toUpperCase();
    const planName = String(serviceItem.plan?.name || '').trim().toUpperCase();
    return serviceName === 'ERP' || planName === 'ERP';
  }, [isIdCardCatalogService, isBiometricCatalogService, isDigitalMarketingService]);

  const inferErpAmountFixed = (svc = {}) => {
    const billingType = String(svc.erp_billing_type || '').toLowerCase();
    if (billingType === 'fixed') return true;
    if (billingType === 'per_student') return false;
    const entry = String(svc.amount_entry || '').trim();
    if (entry === 'Up to student') return true;
    if (entry === 'Number of students') return false;
    const qty = parseFloat(svc.item_count || 0) || 0;
    return qty <= 1;
  };

  const getMonthCountBetweenDates = (startDateStr, endDateStr) => {
    const start = parseBillingDateOnly(startDateStr);
    const end = parseBillingDateOnly(endDateStr);
    if (!start || !end || end < start) return 0;
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1;
    return months > 0 ? months : 0;
  };

  const getInclusiveYearCountBetweenDates = (startDateStr, endDateStr) => {
    const from = parseBillingDateOnly(startDateStr);
    const to = parseBillingDateOnly(endDateStr);
    if (!from || !to || to < from) return 0;
    let years = to.getFullYear() - from.getFullYear();
    if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
      years += 1;
    }
    return years > 0 ? years : 0;
  };

  const getDigitalMarketingCalculationType = React.useCallback((serviceObj = {}, planObj = {}) => {
    const direct = serviceObj?.calculation_type || serviceObj?.digital_calculation_type || planObj?.calculation_type || planObj?.digital_calculation_type;
    if (direct) return direct;
    const features = Array.isArray(planObj?.digital_marketing_features) ? planObj.digital_marketing_features : [];
    const first = features[0];
    if (first && typeof first === 'object') return first.calculation_type || 'monthwise';
    return 'monthwise';
  }, []);

  const getDigitalMarketingQuantity = React.useCallback((serviceId, serviceObj, planObj, detailsOverride) => {
    const details = detailsOverride || setup_details;
    const calculationType = getDigitalMarketingCalculationType(serviceObj, planObj);
    if (calculationType === 'yearwise') {
      const y = getInclusiveYearCountBetweenDates(
        details[`order_date_${serviceId}`],
        details[`service_end_date_${serviceId}`],
      );
      return y > 0 ? y : 0;
    }
    if (calculationType === 'studentcount') {
      return parseFloat(overallFetchedData.student_strength || details.student_strength || 0) || 0;
    }
    return getMonthCountBetweenDates(
      details[`order_date_${serviceId}`],
      details[`service_end_date_${serviceId}`]
    );
  }, [getDigitalMarketingCalculationType, overallFetchedData.student_strength, setup_details]);


  React.useEffect(() => {
    initializeModules();
    if (config_id) {
      fetchSavedData();
    } else {
      alert('Company mapping not created');
      navigate(-1); // ✅ correct
    }
  }, []);

  React.useEffect(() => {
    if (!expandedInitRef.current && otherServices.length > 0) {
      const init = {};
      otherServices.forEach(s => { init[s.id] = !config_id; });
      setServiceExpanded(init);
      expandedInitRef.current = true;
    }
  }, [otherServices]);

  const initializeModules = () => {
    const modules = {};
    Object.keys(ModulesConfig).forEach(moduleKey => {
      modules[moduleKey] = {
        checked: false,
        options: {}
      };
      ModulesConfig[moduleKey].options.forEach(option => {
        modules[moduleKey].options[option.id] = false;
      });
    });
    setSelectedModules(modules);
  };

  const updateDetails = (details) => {
    let field_details_temp = { pricing: [], additional: [] };
    let setup_details_temp = {};
    let value = '';

    let pricingDetailsTemp = _.cloneDeep(form_details['pricing_details']['list']);
    pricingDetailsTemp.forEach((data) => {
      if (details && details[data['name']] !== undefined && details[data['name']] !== null && details[data['name']] !== '') {
        value = details[data['name']];
      } else {
        value = data['default'];
      }
      data['default'] = value;
      setup_details_temp[data['name']] = value;
      field_details_temp['pricing'].push(data);
    });
    set_field_details(() => field_details_temp);
    set_setup_details(() => setup_details_temp);
    set_loading(() => false);
  };

  const fetchSavedData = () => {
    try {
      getRequest(`${GET_URL.billing_config.api}${config_id}/`, {}, props).then(response => {
        const payload = response?.data?.data || response?.data;
        if (!payload) { updateDetails(); return; }

        payload.student_strength = resolveStudentStrength(payload);

        // Pricing details
        updateDetails(payload);
        console.log('here2',payload)

        // Modules
        if (payload.modules_allowed) {
          const modules = {};
          Object.keys(ModulesConfig).forEach(moduleKey => {
            modules[moduleKey] = {
              checked: !!payload.modules_allowed[moduleKey]?.enabled,
              options: { ...ModulesConfig[moduleKey].options.reduce((acc, opt) => ({ ...acc, [opt.id]: false }), {}), ...(payload.modules_allowed[moduleKey]?.options || {}) }
            };
          });
          setSelectedModules(modules);
        }
        setoverallFetchedData(payload)
        // Services
        if (Array.isArray(payload.pricing_services)) {
          const hasDigital = payload.pricing_services.some(svc => svc.digitalservice != null);
          if (hasDigital && GET_URL.digitalmarketingfeature?.api) {
            getRequest(GET_URL.digitalmarketingfeature.api, { limit: 500, pageno: 1 }, props)
              .then(digRes => {
                const digRaw = digRes?.data?.data || digRes?.data || [];
                const digList = Array.isArray(digRaw) ? digRaw : [];
                applySavedServices(payload, digList);
              })
              .catch(() => applySavedServices(payload, []));
          } else {
            applySavedServices(payload, []);
          }
        }
      }).catch(() => {
        updateDetails();
      });
    } catch {
      updateDetails();
    }
  };

  const buildInvoicedSnapshot = (svcSaved) => ({
    service_amount: svcSaved.service_amount ?? '',
    service_gst_percentage: svcSaved.service_gst_percentage ?? '',
    service_duration: svcSaved.service_duration ?? '',
    order_date: svcSaved.order_date ?? '',
    service_end_date: svcSaved.service_end_date ?? '',
    item_count: svcSaved.item_count ?? '',
    payment_option: svcSaved.payment_option ?? 'with_gst_invoice_acc',
    notes: svcSaved.notes ?? '',
    payment_terms: (Array.isArray(svcSaved.payment_terms) ? svcSaved.payment_terms : []).map((t) => ({
      id: t.id,
      term_name: t.term_name || '',
      term_receive_date: t.term_receive_date || '',
      amount_percentage: t.amount_percentage ?? null,
      amount_rs: t.amount_rs ?? null,
    })),
  });

  const applySavedServices = (svcData, digitalServicesList = []) => {
    const servicesList = svcData.pricing_services;
    // Build otherServices with minimal service/plan info
    const builtServices = servicesList.map((svc, idx) => {
      let resolvedName = svc.service_name;
      if (svc.digitalservice != null) {
        const digSvc = digitalServicesList.find(d => String(d.id) === String(svc.digitalservice));
        if (digSvc && digSvc.name) resolvedName = digSvc.name;
      }
      // Lock only when issue invoice is INVOICED (or paid); DRAFT/ISSUED stay editable.
      const lockingIssueStatuses = new Set(['INVOICED', 'PARTIAL_PAID', 'PAID']);
      const hasInvoicedIssue = (Array.isArray(svc.issue_invoice_items) ? svc.issue_invoice_items : [])
        .some((it) => lockingIssueStatuses.has(String(it.status || '').toUpperCase()));
      const hasInvoice = (
        hasInvoicedIssue
        || (Array.isArray(svc.invoice_items) && svc.invoice_items.length > 0)
      );
      const idCardConfig = parseIdCardConfigFromNotes(svc.notes);
      const biometricConfig = parseBiometricConfigFromNotes(svc.notes);
      const biometricCode = biometricConfig
        ? (biometricConfig.kind === 'manual' ? BIOMETRIC_KINDS.manual.code : BIOMETRIC_KINDS.auto.code)
        : undefined;
      if (biometricConfig) {
        resolvedName = biometricConfig.kind === 'manual' ? BIOMETRIC_KINDS.manual.name : BIOMETRIC_KINDS.auto.name;
      }
      return {
        id: idx,
        pk: svc.id,
        hasInvoice,
        invoicedSnapshot: hasInvoice ? buildInvoicedSnapshot(svc) : undefined,
        idCardConfig: idCardConfig || undefined,
        biometricConfig: biometricConfig || undefined,
        notes: svc.notes || '',
        erpAmountFixed: inferErpAmountFixed(svc),
        service: {
          id: svc.service_id,
          name: resolvedName,
          code: idCardConfig ? 'ID_CARDS' : biometricCode,
          digitalservice: svc.digitalservice ?? null,
          agreement_erp_id: svc.agreement_erp_id ?? null,
          digital_calculation_type: svc.digital_calculation_type || 'monthwise',
        },
        plan: {
          id: svc.plan_id,
          name: svc.plan_name,
          billing_name: svc.billing_name,
          billing_cycle: svc.billing_cycle || 'termly',
          service_type: svc.service_type || '',
          digital_marketing_features: svc.digital_marketing_features,
          digital_calculation_type: svc.digital_calculation_type || 'monthwise',
        },
      };
    });
    setOtherServices(builtServices);

    // Build per-service field details and setup values
    const fieldMap = {};
    const resolvedStudents = resolveStudentStrength(svcData);
    const newSetup = { ...setup_details, student_strength: resolvedStudents };
    const serviceTermDetailsTemp = {}
    const nextServiceTerms = {};
    const nextIdCardAddGst = {};
    builtServices.forEach(({ id, service, plan, idCardConfig, biometricConfig, hasInvoice, erpAmountFixed, notes }, index) => {
      const svcSaved = servicesList[index];
      const detailsTemplate = _.cloneDeep(form_details['pricing_services']['list']);
      const serviceFields = [];
      const isIdCardConfiguredLine = Boolean(idCardConfig);
      const isIdCardLine = isIdCardConfiguredLine || isIdCardCatalogService(service, plan);
      const isBiometricConfiguredLine = Boolean(biometricConfig);
      const isErpLine = isErpServiceLine({
        id, service, plan, idCardConfig, biometricConfig, notes, erpAmountFixed,
      });
      detailsTemplate.forEach((data) => {
        if (data.name === 'service_name') return;
        const fieldCopy = { ...data };
        fieldCopy.name = `${data.name}_${id}`;
        if (hasInvoice) {
          fieldCopy.disabled = true;
        }
        let defVal = svcSaved[data.name];
        if (data.name === 'item_count') {
          fieldCopy.hidden = plan?.service_type !== 'ITEM_WISE' || isDigitalMarketingService(service, plan) || isIdCardConfiguredLine || isBiometricConfiguredLine;
        }
        if (data.name === 'service_amount' && isErpLine) {
          fieldCopy.label = erpAmountFixed
            ? 'Fixed amount (before GST)'
            : 'Amount per student (before GST)';
        }
        if (data.name === 'service_gst_percentage') {
          // ID card GST is controlled by the Add GST toggle. Hide the generic GST field.
          if (isIdCardConfiguredLine || isIdCardLine) {
            fieldCopy.hidden = true;
            fieldCopy.required = false;
          }
        }
        if (isIdCardConfiguredLine && (data.name === 'service_amount' || data.name === 'item_count' || data.name === 'notes')) {
          fieldCopy.hidden = true;
        }
        if (isBiometricConfiguredLine && (data.name === 'service_amount' || data.name === 'item_count' || data.name === 'notes')) {
          fieldCopy.hidden = true;
        }
        fieldCopy.default = defVal ?? '';
        serviceFields.push(fieldCopy);
        newSetup[fieldCopy.name] = fieldCopy.default;
        if (data.name === 'item_count') {
          if (isIdCardConfiguredLine && idCardConfig) {
            newSetup[fieldCopy.name] = 1;
          } else if (isBiometricConfiguredLine && biometricConfig) {
            newSetup[fieldCopy.name] = parseFloat(biometricConfig.deviceCount) || svcSaved.item_count || 1;
          } else if (isIdCardLine && plan?.service_type === 'STUDENT_WISE') {
            const savedIc = svcSaved.item_count;
            const parsedSaved =
              savedIc != null && savedIc !== '' && !Number.isNaN(parseFloat(savedIc))
                ? parseFloat(savedIc)
                : null;
            // Keep saved line qty (e.g. agreement ERP uses item_count=1 with service_amount already
            // as full licence base). Do not replace with student_strength — that would re-multiply in pricing.
            if (parsedSaved != null && parsedSaved > 0) {
              newSetup[fieldCopy.name] = parsedSaved;
            }
            else {
              newSetup[fieldCopy.name] = resolvedStudents;
            }
          } else if (isDigitalMarketingService(service, plan)) {
            newSetup[fieldCopy.name] = getDigitalMarketingQuantity(id, service, plan, {
              ...newSetup,
              [`order_date_${id}`]: svcSaved.order_date,
              [`service_end_date_${id}`]: svcSaved.service_end_date,
            });
          }
        }
      });
      newSetup[`payment_option_${id}`] =
        svcSaved.payment_option != null && svcSaved.payment_option !== ''
          ? svcSaved.payment_option
          : 'with_gst_invoice_acc';
      newSetup[`service_name_${id}`] = service.name || '';
      if (idCardConfig) {
        const total = calculateIdCardConfigTotal(idCardConfig);
        newSetup[`service_amount_${id}`] = total > 0 ? total.toFixed(2) : (svcSaved.service_amount || '');
        newSetup[`item_count_${id}`] = 1;
      }
      if (isIdCardLine || idCardConfig) {
        const savedGst = parseFloat(svcSaved.service_gst_percentage);
        nextIdCardAddGst[id] = !Number.isNaN(savedGst) && savedGst > 0;
      }
      if (biometricConfig) {
        newSetup[`item_count_${id}`] = parseFloat(biometricConfig.deviceCount) || svcSaved.item_count || '';
        newSetup[`service_amount_${id}`] = biometricConfig.fee !== '' && biometricConfig.fee != null
          ? String(biometricConfig.fee)
          : (svcSaved.service_amount || '');
      }
      fieldMap[id] = serviceFields;

      // Digital orders use month-wise billing from order/service dates, not payment terms.
      const isDigital = isDigitalMarketingService(service, plan);
      const svcTerms =
        isDigital ? [] : Array.isArray(svcSaved.payment_terms) ? svcSaved.payment_terms : [];
      const termsArr = svcTerms.map((_, tIdx) => ({ id: tIdx }));
      serviceTermDetailsTemp[id] = {};
      svcTerms.forEach((t, tIdx) => {
        const pctFilled = isTermAmountFilled(t.amount_percentage);
        const rsFilled = isTermAmountFilled(t.amount_rs);
        serviceTermDetailsTemp[id][tIdx] = {
          pk: t.id,
          term_name: t.term_name || `Term ${tIdx + 1}`,
          term_receive_date: t.term_receive_date || '',
          // Prefer % when both are somehow set; never treat 0.00 default as filled.
          amount_percentage: pctFilled ? String(t.amount_percentage) : '',
          amount_rs: !pctFilled && rsFilled ? String(t.amount_rs) : '',
        };
      });
      nextServiceTerms[id] = termsArr;
      setServiceTermErrors(prev => ({ ...prev, [id]: {} }));
      setServiceTermValidationErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
    });
    setServiceTermDetails(serviceTermDetailsTemp);
    setServiceTerms(nextServiceTerms);
    setIdCardAddGst(nextIdCardAddGst);

    setServiceFieldDetails(fieldMap);
    set_setup_details(newSetup);
  };

  const removeAllServiceTerms = (svcId) => {
    for (const val of Object.values(serviceTermDetails[svcId])) {
      if (val.pk && !removedTermIds.current.includes(val.pk)) {
        removedTermIds.current.push(val.pk)
      }
    }
  }

  const updateParent = (name, value, list, type) => {
    if (type?.hasInvoice) {
      return;
    }
    const updatedDetails = { ...setup_details, [name]: value };
    const serviceId = type?.id ?? null;

    // When order date is set, auto-fetch end date to +3 years (still editable).
    if (serviceId !== null && name.startsWith('order_date_') && value) {
      const endKey = `service_end_date_${serviceId}`;
      const parsed = parseBillingDateOnly(value) || new Date(value);
      if (parsed && !Number.isNaN(parsed.getTime())) {
        const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        end.setFullYear(end.getFullYear() + 3);
        updatedDetails[endKey] = formatYMD(end);
        // Keep first term receive date in sync with order date when it was empty or matched the previous order date.
        const prevOrderDate = setup_details[`order_date_${serviceId}`] || '';
        const termsForService = serviceTermDetails[serviceId] || {};
        const nextTerms = { ...termsForService };
        let termsChanged = false;
        Object.keys(nextTerms).forEach((termId) => {
          const term = nextTerms[termId] || {};
          const receive = term.term_receive_date || '';
          if (!receive || receive === prevOrderDate) {
            nextTerms[termId] = { ...term, term_receive_date: value };
            termsChanged = true;
          }
        });
        if (termsChanged) {
          setServiceTermDetails((prev) => ({
            ...prev,
            [serviceId]: nextTerms,
          }));
        }
      }
    }

    set_setup_details(updatedDetails);

    // Clear service field errors for this field when it's updated
    if (serviceId !== null && serviceFieldErrors[serviceId]) {
      setServiceFieldErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[serviceId] && newErrors[serviceId][name]) {
          newErrors[serviceId] = { ...newErrors[serviceId] };
          delete newErrors[serviceId][name];
          if (Object.keys(newErrors[serviceId]).length === 0) {
            delete newErrors[serviceId];
          }
        }
        return newErrors;
      });
    }

    if (isDigitalMarketingService(type?.service, type?.plan)) {
      updatedDetails[`item_count_${serviceId}`] = getDigitalMarketingQuantity(serviceId, type.service, type.plan, updatedDetails);
      set_setup_details(updatedDetails);
      return;
    }
  };

  // Service functions
  const openServiceModal = () => {
    setServiceModalOpen(true);
  };

  const closeServiceModal = () => {
    setServiceModalOpen(false);
  };

  const handleServicePlanSelect = (selection) => {
    const { service, plan, idCardSetup, biometricSetup, biometricKind } = selection;
    if (idCardSetup) {
      setPendingIdCardSelection({ service, plan });
      setIdCardConfigModalOpen(true);
      return;
    }
    if (biometricSetup) {
      addServiceFromSelection(service, plan, null, createEmptyBiometricConfig(biometricKind));
      return;
    }
    addServiceFromSelection(service, plan);
  };

  const addServiceFromSelection = (service, plan, idCardConfig, biometricConfig) => {
    const newId = otherServices.length > 0 ? Math.max(...otherServices.map(s => s.id)) + 1 : 0;

    const newService = {
      id: newId,
      service: service,
      plan: plan,
      notes: '',
      // New ERP lines default to per-student; user can switch to fixed total.
      erpAmountFixed: false,
      ...(idCardConfig ? { idCardConfig: normalizeIdCardConfig(idCardConfig) } : {}),
      ...(biometricConfig ? { biometricConfig: normalizeBiometricConfig(biometricConfig) } : {}),
    };

    setOtherServices([...otherServices, newService]);
    setServiceExpanded(prev => ({ ...prev, [newId]: true }));

    initializeServiceFieldsFromPlan(newId, service, plan, idCardConfig, biometricConfig, {
      erpAmountFixed: false,
    });
  };

  const updateErpAmountFieldLabel = (serviceId, amountFixed) => {
    setServiceFieldDetails((prev) => {
      const fields = prev[serviceId];
      if (!Array.isArray(fields)) return prev;
      const amountKey = `service_amount_${serviceId}`;
      const currentAmount = setup_details[amountKey];
      return {
        ...prev,
        [serviceId]: fields.map((field) => {
          if (field?.name !== amountKey) return field;
          return {
            ...field,
            label: amountFixed
              ? 'Fixed amount (before GST)'
              : 'Amount per student (before GST)',
            // Keep DynamicForm display in sync with setup_details (no silent multiply).
            default: currentAmount != null && currentAmount !== '' ? currentAmount : field.default,
          };
        }),
      };
    });
  };

  const handleErpAmountFixedChange = (serviceId, amountFixed) => {
    const svc = otherServices.find((s) => s.id === serviceId);
    if (!svc || svc.hasInvoice) return;
    const students = parseFloat(setup_details.student_strength || overallFetchedData.student_strength || 0) || 0;

    setOtherServices((prev) =>
      prev.map((s) => (s.id === serviceId ? { ...s, erpAmountFixed: Boolean(amountFixed) } : s)),
    );
    updateErpAmountFieldLabel(serviceId, amountFixed);

    // Keep the entered amount as-is:
    // Fixed = that amount (+ GST). Per student = that amount × students (+ GST).
    set_setup_details((prev) => ({
      ...prev,
      [`item_count_${serviceId}`]: amountFixed ? 1 : (students > 0 ? students : 1),
    }));
  };

  const handleIdCardAddGstChange = (serviceId, enabled) => {
    const svc = otherServices.find((s) => s.id === serviceId);
    if (!svc || svc.hasInvoice || !isIdCardGstEligibleLine(svc)) return;
    setIdCardAddGst((prev) => ({ ...prev, [serviceId]: Boolean(enabled) }));
    set_setup_details((prev) => {
      const current = parseFloat(prev[`service_gst_percentage_${serviceId}`]) || 0;
      const nextPct = enabled
        ? (current > 0 ? current : resolveDefaultIdCardGstPercent(svc.plan))
        : 0;
      return {
        ...prev,
        [`service_gst_percentage_${serviceId}`]: nextPct,
        [`payment_option_${serviceId}`]: enabled ? 'with_gst_invoice_acc' : 'without_gst_invoice_acc',
      };
    });
  };

  const handleIdCardGstPercentChange = (serviceId, value) => {
    const svc = otherServices.find((s) => s.id === serviceId);
    if (!svc || svc.hasInvoice || !isIdCardGstEligibleLine(svc)) return;
    if (value !== '' && value != null) {
      const n = parseFloat(value);
      if (!Number.isNaN(n) && n > 100) return;
    }
    set_setup_details((prev) => ({
      ...prev,
      [`service_gst_percentage_${serviceId}`]: value,
    }));
  };

  const handleIdCardConfigConfirm = (config) => {
    if (!pendingIdCardSelection) return;
    const { service, plan } = pendingIdCardSelection;
    addServiceFromSelection(service, plan, config);
    setPendingIdCardSelection(null);
    setIdCardConfigModalOpen(false);
  };

  const handleIdCardConfigChange = (serviceId, config) => {
    const normalized = normalizeIdCardConfig(config);
    setOtherServices((prev) =>
      prev.map((svc) => (svc.id === serviceId ? { ...svc, idCardConfig: normalized } : svc)),
    );
    syncIdCardPricingFromConfig(serviceId, normalized);
  };

  const handleBiometricConfigChange = (serviceId, config) => {
    const normalized = normalizeBiometricConfig(config);
    setOtherServices((prev) =>
      prev.map((svc) => (svc.id === serviceId ? { ...svc, biometricConfig: normalized } : svc)),
    );
    syncBiometricPricingFromConfig(serviceId, normalized);
  };

  const resolveUnitPriceFromSelection = (service, plan) => {
    // Digital features carry their own price; catalog plan is only a carrier template.
    if (isDigitalMarketingService(service, plan) && service?.price != null && service.price !== '') {
      return String(service.price);
    }
    if (plan?.base_price != null && plan.base_price !== '') {
      return String(plan.base_price);
    }
    return '';
  };

  const initializeServiceFieldsFromPlan = (serviceId, service, plan, idCardConfig, biometricConfig, options = {}) => {
    let serviceDetailsTemp = _.cloneDeep(form_details['pricing_services']['list']);
    let serviceFields = [];
    const isDigital = isDigitalMarketingService(service, plan);
    const isIdCardConfiguredLine = Boolean(idCardConfig);
    const isBiometricConfiguredLine = Boolean(biometricConfig);
    const isIdCardLine = isIdCardConfiguredLine || isIdCardCatalogService(service, plan);
    const isErpLine = isErpServiceLine({
      service,
      plan,
      idCardConfig,
      biometricConfig,
      notes: '',
    });
    const erpAmountFixed = Boolean(options.erpAmountFixed);
    const isStudentCountLine =
      !isDigital && !isIdCardConfiguredLine && !isBiometricConfiguredLine && (
        plan?.service_type === 'STUDENT_WISE'
        || isIdCardCatalogService(service, plan)
        || (isErpLine && !erpAmountFixed)
      );
    const unitPrice = idCardConfig
      ? String(calculateIdCardConfigTotal(idCardConfig) || '')
      : biometricConfig
        ? String(biometricConfig.fee || '')
        : resolveUnitPriceFromSelection(service, plan);

    serviceDetailsTemp.forEach((data) => {
      // Skip service_name as it's displayed as a Chip
      if (data.name === 'service_name') {
        return;
      }

      const fieldCopy = { ...data };
      fieldCopy.name = `${data.name}_${serviceId}`;

      // Pre-fill values from plan / digital feature price
      if (data.name === 'service_amount') {
        fieldCopy.default = unitPrice;
        if (isIdCardConfiguredLine || isBiometricConfiguredLine) fieldCopy.hidden = true;
        if (isErpLine) {
          fieldCopy.label = erpAmountFixed
            ? 'Fixed amount (before GST)'
            : 'Amount per student (before GST)';
        }
      } else if (data.name === 'service_gst_percentage') {
        // Item-wise configured ID cards stay GST-free. Default ID cards use Add GST toggle.
        if (isIdCardConfiguredLine || isIdCardLine) {
          fieldCopy.default = isIdCardConfiguredLine ? 0 : (plan && plan.default_tax_amount !== undefined && plan.default_tax_amount !== null ? plan.default_tax_amount : 0);
          fieldCopy.hidden = true;
          fieldCopy.required = false;
        } else {
          // Use plan default_tax_amount if available; digital defaults to 18%.
          if (plan && plan.default_tax_amount !== undefined && plan.default_tax_amount !== null) {
            fieldCopy.default = plan.default_tax_amount;
          } else if (isDigital) {
            fieldCopy.default = 18;
          } else {
            fieldCopy.default = '';
          }
        }
      } else if (data.name === 'service_duration') {
        fieldCopy.default = (plan && plan.billing_cycle === 'monthly') ? 1 : '';
        // Digital services don't need service_duration for billing calculation
        if (isDigital) fieldCopy.required = false;
      } else if (data.name === 'item_count') {
        fieldCopy.default = isIdCardConfiguredLine ? 1 : (isBiometricConfiguredLine ? (biometricConfig.deviceCount || '') : (isStudentCountLine ? (overallFetchedData['student_strength'] || '') : 1));
        fieldCopy.hidden = plan?.service_type !== 'ITEM_WISE' || isDigital || isIdCardConfiguredLine || isBiometricConfiguredLine;
      } else if (data.name === 'order_date') {
        // Use YYYY-MM-DD string for maxDate so DynamicForm string comparison works correctly
        fieldCopy.default = isDigital ? '' : formatYMD(new Date());
        fieldCopy.maxDate = formatYMD(new Date());  // string, not Date object
        fieldCopy.minDate = '2020-01-02';           // string, not Date object
      } else if (data.name === 'service_end_date') {
        if (isDigital) {
          fieldCopy.default = '';
        } else {
          const end = new Date();
          end.setFullYear(end.getFullYear() + 3);
          fieldCopy.default = formatYMD(end);
        }
        fieldCopy.minDate = formatYMD(new Date());  // string, not Date object
      } else if (data.name === 'notes') {
        fieldCopy.default = '';
        if (isIdCardConfiguredLine || isBiometricConfiguredLine) fieldCopy.hidden = true;
      } else {
        fieldCopy.default = '';
      }

      serviceFields.push(fieldCopy);
    });

    setServiceFieldDetails(prev => ({
      ...prev,
      [serviceId]: serviceFields
    }));

    // Also update setup_details with the values
    const newSetupDetails = { ...setup_details };
    newSetupDetails[`service_name_${serviceId}`] = service.name || '';
    newSetupDetails[`service_amount_${serviceId}`] = unitPrice;
    if (isIdCardConfiguredLine) {
      newSetupDetails[`item_count_${serviceId}`] = 1;
      newSetupDetails[`service_gst_percentage_${serviceId}`] = 0;
    } else if (isBiometricConfiguredLine) {
      newSetupDetails[`item_count_${serviceId}`] = biometricConfig.deviceCount || '';
      newSetupDetails[`service_amount_${serviceId}`] = biometricConfig.fee || '';
    } else if (isStudentCountLine) {
      newSetupDetails[`item_count_${serviceId}`] = overallFetchedData['student_strength'] || '';
    } else if (newSetupDetails[`item_count_${serviceId}`] === undefined) {
      newSetupDetails[`item_count_${serviceId}`] = 1;
    }
    if (isDigital) {
      const quantity = getDigitalMarketingQuantity(serviceId, service, plan, newSetupDetails);
      newSetupDetails[`item_count_${serviceId}`] = quantity > 0 ? quantity : '';
      if (plan && plan.default_tax_amount !== undefined && plan.default_tax_amount !== null) {
        newSetupDetails[`service_gst_percentage_${serviceId}`] = plan.default_tax_amount;
      } else {
        newSetupDetails[`service_gst_percentage_${serviceId}`] = 18;
      }
    } else if (isIdCardCatalogService(service, plan) && !isIdCardConfiguredLine) {
      newSetupDetails[`service_gst_percentage_${serviceId}`] = 0;
    } else if (
      !isIdCardConfiguredLine
      && plan
      && plan.default_tax_amount !== undefined
      && plan.default_tax_amount !== null
    ) {
      newSetupDetails[`service_gst_percentage_${serviceId}`] = plan.default_tax_amount;
    }
    if (plan && plan.billing_cycle === 'monthly') {
      newSetupDetails[`service_duration_${serviceId}`] = 1;
    }
    // Set order_date default only for non-digital services.
    // Service end date defaults to 3 years from order date (editable).
    const defaultOrderDate = isDigital ? '' : formatYMD(new Date());
    newSetupDetails[`order_date_${serviceId}`] = defaultOrderDate;
    if (defaultOrderDate) {
      const end = new Date();
      end.setFullYear(end.getFullYear() + 3);
      newSetupDetails[`service_end_date_${serviceId}`] = formatYMD(end);
    } else {
      newSetupDetails[`service_end_date_${serviceId}`] = '';
    }
    // Set payment_option default to 'with_gst_invoice_acc'
    newSetupDetails[`payment_option_${serviceId}`] = 'with_gst_invoice_acc';

    set_setup_details(newSetupDetails);
    if (isIdCardCatalogService(service, plan) || isIdCardConfiguredLine) {
      setIdCardAddGst((prev) => ({ ...prev, [serviceId]: false }));
    }

    // Default payment terms: 1 term with 100% (editable). Digital services have no payment terms.
    if (isDigitalMarketingService(service, plan)) {
      setServiceTerms(prev => ({ ...prev, [serviceId]: [] }));
      setServiceTermDetails(prev => ({ ...prev, [serviceId]: {} }));
    } else {
      const defaultTermDate = defaultOrderDate || '';
      setServiceTerms(prev => ({
        ...prev,
        [serviceId]: [{ id: 0 }]
      }));
      setServiceTermDetails(prev => ({
        ...prev,
        [serviceId]: {
          0: {
            term_name: 'Term 1',
            term_receive_date: defaultTermDate,
            amount_percentage: '100',
            amount_rs: '',
          }
        }
      }));
      setServiceTermErrors(prev => ({ ...prev, [serviceId]: {} }));
      setServiceTermValidationErrors(prev => {
        const n = { ...prev };
        delete n[serviceId];
        return n;
      });
    }
  };

  const formatYMD = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const generateMonthlyTerms = (serviceId, detailsOverride) => {
    const details = detailsOverride || setup_details;
    const endDateStr = details[`service_end_date_${serviceId}`] || '';
    let start = new Date();
    start = new Date(start.getFullYear(), start.getMonth(), 1);
    let end = endDateStr ? new Date(endDateStr) : new Date(start.getFullYear(), start.getMonth(), 1);
    end = new Date(end.getFullYear(), end.getMonth(), 1);
    let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    if (months < 1 || isNaN(months)) months = 1;

    const terms = Array.from({ length: months }, (_, i) => ({ id: i }));
    const svc = otherServices.find(s => s.id === serviceId);
    // amount_rs is taxable base (ex-GST) so issue-invoice can add GST without double-counting.
    const { baseAmount } = getServicePricingSummary(serviceId, svc?.service, svc?.plan, details);
    const perTermRaw = baseAmount / months;
    const perTermRounded = Math.floor(perTermRaw * 100) / 100;
    const remainder = +(baseAmount - perTermRounded * months).toFixed(2);

    const newDetails = {};
    for (let i = 0; i < months; i++) {
      const termDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const amt = i === months - 1 ? +(perTermRounded + remainder).toFixed(2) : perTermRounded;
      newDetails[i] = { term_name: `Term ${i + 1}`, term_receive_date: formatYMD(termDate), amount_percentage: '', amount_rs: amt.toString() };
    }

    setServiceTerms(prev => ({ ...prev, [serviceId]: terms }));
    setServiceTermDetails(prev => ({ ...prev, [serviceId]: newDetails }));
    setServiceTermErrors(prev => ({ ...prev, [serviceId]: {} }));
    setServiceTermValidationErrors(prev => {
      const n = { ...prev }; delete n[serviceId]; return n;
    });
  };

  const generateOneTimeTerm = (serviceId, detailsOverride) => {
    const details = detailsOverride || setup_details;
    const svc = otherServices.find(s => s.id === serviceId);
    const { baseAmount } = getServicePricingSummary(serviceId, svc?.service, svc?.plan, details);
    const endDateStr = details[`service_end_date_${serviceId}`] || '';
    const date = endDateStr || formatYMD(new Date());
    const terms = [{ id: 0 }];
    const newDetails = { 0: { term_name: 'Term 1', term_receive_date: date, amount_percentage: '', amount_rs: baseAmount.toFixed(2) } };
    setServiceTerms(prev => ({ ...prev, [serviceId]: terms }));
    setServiceTermDetails(prev => ({ ...prev, [serviceId]: newDetails }));
    setServiceTermErrors(prev => ({ ...prev, [serviceId]: {} }));
    setServiceTermValidationErrors(prev => {
      const n = { ...prev }; delete n[serviceId]; return n;
    });
  };

  const getServicePricingSummary = (serviceId, serviceObj, planObj, detailsOverride) => {
    const details = detailsOverride || setup_details;
    const serviceItem = otherServices.find((s) => s.id === serviceId);
    if (serviceItem?.idCardConfig) {
      const baseAmount = calculateIdCardConfigTotal(serviceItem.idCardConfig);
      let gstPercent = parseFloat(details[`service_gst_percentage_${serviceId}`]) || 0;
      if (!idCardAddGst[serviceId]) {
        gstPercent = 0;
      } else if (gstPercent <= 0) {
        gstPercent = resolveDefaultIdCardGstPercent(serviceItem.plan);
      }
      const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
      const totalAmount = baseAmount + gstAmount;
      return {
        baseAmount,
        gstAmount,
        totalAmount,
        gstPercent,
        monthCount: 0,
        calculationType: 'idcard',
        itemCount: 1,
      };
    }
    if (serviceItem?.biometricConfig) {
      const baseAmount = calculateBiometricConfigTotal(serviceItem.biometricConfig);
      const gstPercent = parseFloat(details[`service_gst_percentage_${serviceId}`]) || 0;
      const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
      const totalAmount = baseAmount + gstAmount;
      const itemCount = parseFloat(serviceItem.biometricConfig.deviceCount) || 0;
      return {
        baseAmount,
        gstAmount,
        totalAmount,
        gstPercent,
        monthCount: 0,
        calculationType: 'biometric',
        itemCount,
      };
    }
    const serviceAmount = parseFloat(details[`service_amount_${serviceId}`]) || 0;
    let gstPercent = parseFloat(details[`service_gst_percentage_${serviceId}`]) || 0;
    if (isIdCardGstEligibleLine(serviceItem)) {
      if (!idCardAddGst[serviceId]) {
        gstPercent = 0;
      } else if (gstPercent <= 0) {
        gstPercent = resolveDefaultIdCardGstPercent(serviceItem.plan);
      }
    }
    const monthCount = getMonthCountBetweenDates(
      details[`order_date_${serviceId}`],
      details[`service_end_date_${serviceId}`]
    );
    const digitalQuantity = getDigitalMarketingQuantity(serviceId, serviceObj, planObj, details);
    const calculationType = getDigitalMarketingCalculationType(serviceObj, planObj);
    let itemCount = isDigitalMarketingService(serviceObj, planObj)
      ? digitalQuantity
      : (parseFloat(details[`item_count_${serviceId}`]) || 1);
    let baseAmount = serviceAmount * itemCount;

    // ERP: Fixed = entered amount is taxable base (+ GST).
    // Per student = entered amount × student count, then + GST.
    if (serviceItem && isErpServiceLine(serviceItem)) {
      if (serviceItem.erpAmountFixed) {
        itemCount = 1;
        baseAmount = serviceAmount;
      } else {
        const fromStrength = parseFloat(
          details.student_strength || overallFetchedData.student_strength || 0,
        ) || 0;
        const fromItem = parseFloat(details[`item_count_${serviceId}`] || 0) || 0;
        const students = fromStrength > 1 ? fromStrength : (fromItem > 1 ? fromItem : (fromStrength || fromItem));
        itemCount = students > 0 ? students : 1;
        baseAmount = serviceAmount * itemCount;
      }
    }

    const gstAmount = gstPercent > 0 ? (baseAmount * (gstPercent / 100)) : 0;
    const totalAmount = baseAmount + gstAmount;
    return { baseAmount, gstAmount, totalAmount, gstPercent, monthCount, calculationType, itemCount };
  };

  const removeService = (serviceId) => {
    // Find the service to get its pk (service order ID)
    const serviceToRemove = otherServices.find(s => s.id === serviceId);

    // Invoiced services cannot be deleted — only new (unsaved) lines can be removed freely.
    if (serviceToRemove?.hasInvoice) {
      Swal.fire({
        icon: 'error',
        title: 'Cannot delete',
        text: 'Invoice created for this order. Cancel the invoice before deleting.',
      });
      return;
    }

    // If service has a pk (existing service order), call DELETE API for soft delete
    if (serviceToRemove?.pk) {
      const url = `${GET_URL.service_order.api}${serviceToRemove.pk}/`;
      deleteRequest(url, {}, props)
        .then((response) => {
          if (response && response.status === 200) {
            // Service order deleted successfully - continue with UI removal
            removedServiceIds.current.push(serviceToRemove.pk);
            performServiceRemoval(serviceId);
          }
        })
        .catch((error) => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error?.response?.data?.Reason || error?.response?.data?.detail || 'Failed to delete service order',
          });
        });
    } else {
      // No pk (new service not yet saved), just remove from UI
      performServiceRemoval(serviceId);
    }
  };

  const performServiceRemoval = (serviceId) => {
    setOtherServices(otherServices.filter(s => s.id !== serviceId));
    setServiceExpanded(prev => { const n = { ...prev }; delete n[serviceId]; return n; });
    const newServiceFields = { ...serviceFieldDetails };
    delete newServiceFields[serviceId];
    setServiceFieldDetails(newServiceFields);

    // Remove service field errors
    const newServiceFieldErrors = { ...serviceFieldErrors };
    delete newServiceFieldErrors[serviceId];
    setServiceFieldErrors(newServiceFieldErrors);

    // Remove service payment terms
    const newServiceTerms = { ...serviceTerms };
    delete newServiceTerms[serviceId];
    setServiceTerms(newServiceTerms);

    const newServiceTermDetails = { ...serviceTermDetails };
    delete newServiceTermDetails[serviceId];
    setServiceTermDetails(newServiceTermDetails);

    const newServiceTermErrors = { ...serviceTermErrors };
    delete newServiceTermErrors[serviceId];
    setServiceTermErrors(newServiceTermErrors);

    const newServiceTermValidationErrors = { ...serviceTermValidationErrors };
    delete newServiceTermValidationErrors[serviceId];
    setServiceTermValidationErrors(newServiceTermValidationErrors);
    setIdCardAddGst((prev) => {
      const n = { ...prev };
      delete n[serviceId];
      return n;
    });

    // Remove service data from setup_details
    const newSetupDetails = { ...setup_details };
    Object.keys(newSetupDetails).forEach(key => {
      if (key.endsWith(`_${serviceId}`)) {
        delete newSetupDetails[key];
      }
    });
    set_setup_details(newSetupDetails);
  };

  // Service payment terms functions
  const addServiceTerm = (serviceId) => {
    const serviceItem = otherServices.find((s) => s.id === serviceId);
    if (serviceItem?.hasInvoice) return;
    const serviceTermsList = serviceTerms[serviceId] || [];
    const newTermId = serviceTermsList.length > 0 ? Math.max(...serviceTermsList.map(t => t.id)) + 1 : 0;
    const termNumber = serviceTermsList.length + 1; // 1-indexed for display

    setServiceTerms(prev => ({
      ...prev,
      [serviceId]: [...serviceTermsList, { id: newTermId }]
    }));

    setServiceTermDetails(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [newTermId]: { term_name: `Term ${termNumber}`, term_receive_date: '', amount_percentage: '', amount_rs: '' }
      }
    }));
  };

  const removeServiceTerm = (serviceId, termId) => {
    const serviceItem = otherServices.find((s) => s.id === serviceId);
    if (serviceItem?.hasInvoice) return;
    const serviceTermsList = serviceTerms[serviceId] || [];
    if (serviceTermsList.length <= 1) {
      setServiceTermValidationErrors((prev) => ({
        ...prev,
        [serviceId]: 'At least one payment term is required.',
      }));
      return;
    }
    setServiceTerms((prev) => ({
      ...prev,
      [serviceId]: serviceTermsList.filter((t) => t.id !== termId),
    }));

    const newTermDetails = { ...(serviceTermDetails[serviceId] || {}) };
    if (newTermDetails[termId]?.pk) {
      removedTermIds.current.push(newTermDetails[termId].pk);
    }
    delete newTermDetails[termId];
    setServiceTermDetails((prev) => ({
      ...prev,
      [serviceId]: newTermDetails,
    }));
    setServiceTermErrors((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setServiceTermValidationErrors((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  };

  const handleServiceTermFieldChange = (serviceId, termId, fieldName, value) => {
    const serviceItem = otherServices.find((s) => s.id === serviceId);
    if (serviceItem?.hasInvoice) return;
    const newTermDetails = { ...serviceTermDetails };

    if (!newTermDetails[serviceId]) {
      newTermDetails[serviceId] = {};
    }

    if (!newTermDetails[serviceId][termId]) {
      newTermDetails[serviceId][termId] = { term_name: '', term_receive_date: '', amount_percentage: '', amount_rs: '' };
    }

    // If entering amount_percentage, clear amount_rs and vice versa
    if (fieldName === 'amount_percentage' && value !== '') {
      newTermDetails[serviceId][termId] = {
        ...newTermDetails[serviceId][termId],
        [fieldName]: value,
        amount_rs: ''
      };
    } else if (fieldName === 'amount_rs' && value !== '') {
      newTermDetails[serviceId][termId] = {
        ...newTermDetails[serviceId][termId],
        [fieldName]: value,
        amount_percentage: ''
      };
    } else {
      newTermDetails[serviceId][termId] = {
        ...newTermDetails[serviceId][termId],
        [fieldName]: value
      };
    }

    setServiceTermDetails(newTermDetails);

    // Clear field + summary validation errors for this service while editing
    setServiceTermErrors((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
    setServiceTermValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[serviceId];
      return newErrors;
    });
  };

  const validateServiceTerms = (serviceId) => {
    const serviceTermsList = serviceTerms[serviceId] || [];
    const termDetailsForService = serviceTermDetails[serviceId] || {};
    const errors = {};
    const fail = (message) => {
      setServiceTermValidationErrors((prev) => ({ ...prev, [serviceId]: message }));
      setServiceTermErrors((prev) => ({ ...prev, [serviceId]: errors }));
      return { ok: false, message };
    };

    if (serviceTermsList.length === 0) {
      return fail('At least one payment term is required.');
    }

    let isValid = true;

    // Check if all terms have required fields
    serviceTermsList.forEach(term => {
      const termData = termDetailsForService[term.id];
      if (!termData || !termData.term_name || String(termData.term_name).trim() === '') {
        errors[`${term.id}_term_name`] = 'Term Name is required';
        isValid = false;
      }
      if (!termData || !termData.term_receive_date) {
        errors[`${term.id}_term_receive_date`] = 'Receive Date is required';
        isValid = false;
      }
      if (!termData || (!isTermAmountFilled(termData.amount_percentage) && !isTermAmountFilled(termData.amount_rs))) {
        errors[`${term.id}_amount`] = 'Either Amount in % or Amount in Rs is required';
        isValid = false;
      }
      // Validate max value for amount_rs
      if (termData && isTermAmountFilled(termData.amount_rs) && !isTermAmountFilled(termData.amount_percentage)) {
        const amountRs = parseFloat(termData.amount_rs);
        if (!isNaN(amountRs) && amountRs > 1000000) {
          errors[`${term.id}_amount_rs`] = 'Amount in Rs must not exceed 1,000,000';
          isValid = false;
        }
      }
      if (termData && isTermAmountFilled(termData.amount_percentage)) {
        const pct = parseFloat(termData.amount_percentage);
        if (!isNaN(pct) && pct > 100) {
          errors[`${term.id}_amount`] = 'Amount in % must not exceed 100';
          isValid = false;
        }
      }
    });

    if (!isValid) {
      setServiceTermErrors(prev => ({
        ...prev,
        [serviceId]: errors
      }));
      return { ok: false, message: 'Payment term name, date, or amount is missing.' };
    }

    // Validate that term dates are in ascending order
    if (serviceTermsList.length > 1) {
      for (let i = 0; i < serviceTermsList.length - 1; i++) {
        const currentTermDate = termDetailsForService[serviceTermsList[i].id]?.term_receive_date;
        const nextTermDate = termDetailsForService[serviceTermsList[i + 1].id]?.term_receive_date;

        if (currentTermDate && nextTermDate) {
          const currentDate = new Date(currentTermDate);
          const nextDate = new Date(nextTermDate);

          if (currentDate >= nextDate) {
            return fail(`Term ${i + 1} date must be earlier than Term ${i + 2} date. Please ensure dates are in ascending order.`);
          }
        }
      }
    }

    // % on a term wins; leftover Rs is display-only and must not skip the 100% check.
    const termsWithPercentage = serviceTermsList.filter(term => isTermAmountFilled(termDetailsForService[term.id]?.amount_percentage));
    const termsWithRsOnly = serviceTermsList.filter(term => (
      !isTermAmountFilled(termDetailsForService[term.id]?.amount_percentage)
      && isTermAmountFilled(termDetailsForService[term.id]?.amount_rs)
    ));

    if (termsWithPercentage.length > 0 && termsWithRsOnly.length > 0) {
      return fail('All terms must use the same payment type. Please use either all "Amount in %" or all "Amount in Rs" for all terms.');
    }

    const svc = otherServices.find(s => s.id === serviceId);
    const { baseAmount } = getServicePricingSummary(serviceId, svc?.service, svc?.plan);

    if (termsWithPercentage.length > 0) {
      const sumPercentage = serviceTermsList.reduce((sum, term) => {
        return sum + (parseFloat(termDetailsForService[term.id]?.amount_percentage) || 0);
      }, 0);

      if (Math.abs(sumPercentage - 100) > 0.01) {
        return fail(`Sum of all term percentages must equal 100%. Current sum: ${sumPercentage.toFixed(2)}%`);
      }
    }

    if (termsWithRsOnly.length > 0) {
      const sumRs = serviceTermsList.reduce((sum, term) => {
        return sum + (parseFloat(termDetailsForService[term.id]?.amount_rs) || 0);
      }, 0);

      // amount_rs is taxable base (ex-GST); must sum to order base amount.
      if (Math.abs(sumRs - baseAmount) > 0.01) {
        return fail(`Sum of all term amounts must equal Base Amount (₹${baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Current sum: ₹${sumRs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      }
    }

    setServiceTermErrors(prev => ({
      ...prev,
      [serviceId]: {}
    }));
    setServiceTermValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[serviceId];
      return newErrors;
    });
    return { ok: true, message: '' };
  };

  const handleModuleChange = (moduleKey) => {
    const newModules = { ...selectedModules };
    const newChecked = !newModules[moduleKey].checked;
    newModules[moduleKey].checked = newChecked;

    // Check/uncheck all options when parent is toggled
    Object.keys(newModules[moduleKey].options).forEach(optionKey => {
      newModules[moduleKey].options[optionKey] = newChecked;
    });

    setSelectedModules(newModules);
  };

  const handleOptionChange = (moduleKey, optionId) => {
    const newModules = { ...selectedModules };
    newModules[moduleKey].options[optionId] = !newModules[moduleKey].options[optionId];

    // Update parent checkbox based on children
    const allChecked = Object.values(newModules[moduleKey].options).every(v => v);
    const someChecked = Object.values(newModules[moduleKey].options).some(v => v);
    newModules[moduleKey].checked = allChecked;
    newModules[moduleKey].indeterminate = someChecked && !allChecked;

    setSelectedModules(newModules);
  };


  const validation = () => {
    let pricingValidate = true;
    let additionalValidate = true;
    let fieldErrorsTemp = {};

    field_details['pricing'].forEach((field) => {
      let value = setup_details[field.name];
      let name = field.name;
      if (field.required && !Boolean(value) && value !== 0) {
        fieldErrorsTemp[name] = 'This field is Mandatory';
        pricingValidate = false;
      } else if (field.regex && !field.regex.value.test(value) && value !== '') {
        fieldErrorsTemp[name] = field.regex.errorText;
        pricingValidate = false;
      }
    });

    const studentCount = parseFloat(setup_details.student_strength || overallFetchedData.student_strength || 0) || 0;
    if (studentCount <= 0) {
      fieldErrorsTemp.student_strength = 'Student Count is required';
      pricingValidate = false;
    }

    field_details['additional'].forEach((field) => {
      let value = setup_details[field.name];
      let name = field.name;
      if (field.required && !Boolean(value) && value !== 0) {
        fieldErrorsTemp[name] = 'This field is Mandatory';
        additionalValidate = false;
      } else if (field.regex && !field.regex.value.test(value) && value !== '') {
        fieldErrorsTemp[name] = field.regex.errorText;
        additionalValidate = false;
      }
    });

    set_fieldErrors(() => fieldErrorsTemp);

    // Validate at least one service exists
    if (!otherServices || otherServices.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'At least one service must be added to submit',
        timer: 2000,
        showConfirmButton: false
      });
      return false;
    }

    // Validate required service fields for each service
    let allServicesValid = true;
    const serviceFieldErrorsTemp = {};
    for (const serviceItem of otherServices) {
      if (serviceItem.hasInvoice) {
        continue;
      }
      if (serviceItem.idCardConfig) {
        const { valid, errors } = validateIdCardConfig(serviceItem.idCardConfig);
        if (!valid) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            id_card_config: errors.join(' '),
          };
          allServicesValid = false;
        }
        continue;
      }
      if (serviceItem.biometricConfig) {
        const { valid, errors } = validateBiometricConfig(serviceItem.biometricConfig);
        if (!valid) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
            biometric_config: errors.join(' '),
          };
          allServicesValid = false;
        }
      }

      const serviceErrors = {};
      const allFields = form_details['pricing_services']['list'];

      allFields.forEach(field => {
        const fieldName = `${field.name}_${serviceItem.id}`;
        let value = setup_details[fieldName];

        // Special handling for service_name (it's read-only, comes from service selection)
        if (field.name === 'service_name') {
          value = serviceItem.service?.name || '';
        }

        // Special handling for item_count when service_type is SERVICE_WISE
        if (
          field.name === 'item_count' &&
          ((serviceItem.plan?.service_type === 'SERVICE_WISE' && !isIdCardCatalogService(serviceItem.service, serviceItem.plan)) ||
            isDigitalMarketingService(serviceItem.service, serviceItem.plan))
        ) {
          if (isErpServiceLine(serviceItem) && !serviceItem.erpAmountFixed) {
            value = setup_details.student_strength || overallFetchedData.student_strength || value;
          } else {
            value = isDigitalMarketingService(serviceItem.service, serviceItem.plan)
              ? getDigitalMarketingQuantity(serviceItem.id, serviceItem.service, serviceItem.plan)
              : 1; // SERVICE_WISE always has item_count = 1
          }
        }

        // Validate required fields — treat numeric 0 and string '0' as valid (e.g. GPS has 0% GST)
        // Also skip service_duration required check for digital services (backend doesn't require it)
        const isEmpty = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && isNaN(v));
        const isDigitalSvc = isDigitalMarketingService(serviceItem.service, serviceItem.plan);
        const skipRequired = (isDigitalSvc && field.name === 'service_duration')
          || (serviceItem.biometricConfig && ['item_count', 'service_amount', 'notes'].includes(field.name))
          || (isIdCardGstEligibleLine(serviceItem) && field.name === 'service_gst_percentage');
        // service_name is auto-filled (read-only) and notes is free text — skip nameRegex on both
        const skipRegex = field.name === 'service_name' || field.name === 'notes';
        if (!skipRequired && field.required && isEmpty(value)) {
          serviceErrors[fieldName] = `${field.label} is required`;
          allServicesValid = false;
        } else if (!skipRegex && field.regex && value != null && value !== '' && !field.regex.value.test(value)) {
          // Validate regex pattern
          serviceErrors[fieldName] = field.regex.errorText || `${field.label} is invalid`;
          allServicesValid = false;
        } else if (value && field.maxNumber !== undefined) {
          // Validate max value for all fields (required and optional)
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && numValue > field.maxNumber) {
            serviceErrors[fieldName] = `${field.label} must not exceed ${field.maxNumber.toLocaleString()}`;
            allServicesValid = false;
          }
        }
      });

      if (Object.keys(serviceErrors).length > 0) {
        serviceFieldErrorsTemp[serviceItem.id] = {
          ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
          ...serviceErrors,
        };
      }
      if (isDigitalMarketingService(serviceItem.service, serviceItem.plan)) {
        const from = setup_details[`order_date_${serviceItem.id}`];
        const to = setup_details[`service_end_date_${serviceItem.id}`];
        const months = getMonthCountBetweenDates(from, to);
        const calculationType = getDigitalMarketingCalculationType(serviceItem.service, serviceItem.plan);
        if (!from) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
            [`order_date_${serviceItem.id}`]: 'From Date is required for other service',
          };
          allServicesValid = false;
        }
        if (!to) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
            [`service_end_date_${serviceItem.id}`]: 'To Date is required for other service',
          };
          allServicesValid = false;
        }
        if (calculationType === 'monthwise' && from && to && months <= 0) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
            [`service_end_date_${serviceItem.id}`]: 'To Date should be greater than or equal to From Date',
          };
          allServicesValid = false;
        }
        if (calculationType === 'yearwise' && from && to && getInclusiveYearCountBetweenDates(from, to) <= 0) {
          serviceFieldErrorsTemp[serviceItem.id] = {
            ...(serviceFieldErrorsTemp[serviceItem.id] || {}),
            [`service_end_date_${serviceItem.id}`]: 'Enter a billing period that spans at least one year',
          };
          allServicesValid = false;
        }
      }
    }
    setServiceFieldErrors(serviceFieldErrorsTemp);

    // Validate service terms (name, date, amounts, sums) in one place
    let allServiceTermsValid = true;
    const serviceTermErrorLines = [];
    for (const serviceItem of otherServices) {
      if (serviceItem.hasInvoice) {
        continue;
      }
      if (isDigitalMarketingService(serviceItem.service, serviceItem.plan)) {
        continue;
      }
      const result = validateServiceTerms(serviceItem.id);
      if (!result?.ok) {
        allServiceTermsValid = false;
        const svcName = serviceItem.service?.name || `Service ${serviceItem.id}`;
        serviceTermErrorLines.push(`• [${svcName}] ${result?.message || 'Payment terms are invalid'}`);
      }
    }
    // eslint-disable-next-line no-console
    console.log('[SUBMIT DEBUG] pricingValidate:', pricingValidate, '| additionalValidate:', additionalValidate, '| allServicesValid:', allServicesValid, '| allServiceTermsValid:', allServiceTermsValid);
    // eslint-disable-next-line no-console
    console.log('[SUBMIT DEBUG] serviceFieldErrors:', serviceFieldErrorsTemp);
    // eslint-disable-next-line no-console
    console.log('[SUBMIT DEBUG] setup_details:', setup_details);
    // eslint-disable-next-line no-console
    console.log('[SUBMIT DEBUG] otherServices:', otherServices);
    if (pricingValidate && additionalValidate && allServicesValid && allServiceTermsValid) {
      // Prepare services data with payment terms
      const services = otherServices.map(serviceItem => {
        const serviceData = {
          id: serviceItem?.pk || undefined,
          service_id: serviceItem.service?.id ?? null,
          service_name: serviceItem.service?.name || '',
          plan_id: serviceItem.plan?.id ?? null,
          plan_name: serviceItem.plan?.name || '',
          // Use ?? so a digitalservice ID of 0 is preserved (not replaced with null)
          digitalservice: serviceItem.service?.digitalservice ?? null,
        };
        if (serviceItem.service?.agreement_erp_id != null && serviceItem.service.agreement_erp_id !== '') {
          serviceData.erpservice = serviceItem.service.agreement_erp_id;
          serviceData.agreement_erp_id = serviceItem.service.agreement_erp_id;
        }
        const invoicedSnap = serviceItem.hasInvoice ? serviceItem.invoicedSnapshot : null;
        form_details['pricing_services']['list'].forEach(field => {
          if (field.name !== 'service_name') {
            if (invoicedSnap && Object.prototype.hasOwnProperty.call(invoicedSnap, field.name)) {
              serviceData[field.name] = invoicedSnap[field.name];
            } else {
              serviceData[field.name] = setup_details[`${field.name}_${serviceItem.id}`] || '';
            }
          }
        });
        serviceData.payment_option = invoicedSnap
          ? invoicedSnap.payment_option
          : (setup_details[`payment_option_${serviceItem.id}`] || 'with_gst_invoice_acc');
        if (serviceItem.idCardConfig && !serviceItem.hasInvoice) {
          serviceData.notes = serializeIdCardConfigToNotes(serviceItem.idCardConfig);
          serviceData.item_count = 1;
          serviceData.service_amount = calculateIdCardConfigTotal(serviceItem.idCardConfig).toFixed(2);
        }
        if (isIdCardGstEligibleLine(serviceItem) && !serviceItem.hasInvoice) {
          const gstOn = Boolean(idCardAddGst[serviceItem.id]);
          const enteredGst = parseFloat(setup_details[`service_gst_percentage_${serviceItem.id}`]) || 0;
          const gstPct = gstOn ? (enteredGst > 0 ? enteredGst : resolveDefaultIdCardGstPercent(serviceItem.plan)) : 0;
          serviceData.service_gst_percentage = gstPct;
          serviceData.payment_option = gstOn ? 'with_gst_invoice_acc' : 'without_gst_invoice_acc';
        }
        if (serviceItem.biometricConfig && !serviceItem.hasInvoice) {
          serviceData.notes = serializeBiometricConfigToNotes(serviceItem.biometricConfig);
          serviceData.item_count = parseFloat(serviceItem.biometricConfig.deviceCount) || 0;
          serviceData.service_amount = parseFloat(serviceItem.biometricConfig.fee) || 0;
        }
        if (
          !serviceItem.hasInvoice
          && (serviceItem.plan?.service_type === 'SERVICE_WISE')
          && !isIdCardCatalogService(serviceItem.service, serviceItem.plan)
          && !serviceItem.biometricConfig
          && !(isErpServiceLine(serviceItem) && !serviceItem.erpAmountFixed)
        ) {
          serviceData['item_count'] = 1;
        }
        if (isErpServiceLine(serviceItem) && !serviceItem.hasInvoice) {
          const students = parseFloat(
            setup_details.student_strength || overallFetchedData.student_strength || 0,
          ) || 0;
          if (serviceItem.erpAmountFixed) {
            serviceData.item_count = 1;
          } else {
            if (students <= 0) {
              throw new Error('Student Count is required for per-student ERP billing.');
            }
            serviceData.item_count = students;
          }
        }
        if (isDigitalMarketingService(serviceItem.service, serviceItem.plan) && !serviceItem.hasInvoice) {
          const calculationType = getDigitalMarketingCalculationType(serviceItem.service, serviceItem.plan);
          const monthCount = getMonthCountBetweenDates(
            setup_details[`order_date_${serviceItem.id}`],
            setup_details[`service_end_date_${serviceItem.id}`]
          );
          const digitalQuantity = getDigitalMarketingQuantity(serviceItem.id, serviceItem.service, serviceItem.plan);
          if (calculationType === 'monthwise' && monthCount <= 0) {
            throw new Error(`Other service "${serviceItem.service?.name || ''}" requires valid From/To dates.`);
          }
          if (calculationType === 'yearwise' && digitalQuantity <= 0) {
            throw new Error(`Other service "${serviceItem.service?.name || ''}" requires valid From/To dates for yearly billing.`);
          }
          if (calculationType === 'studentcount' && digitalQuantity <= 0) {
            throw new Error(`Other service "${serviceItem.service?.name || ''}" requires student strength.`);
          }
          serviceData['item_count'] = digitalQuantity;
          serviceData['digital_calculation_type'] = calculationType;
          serviceData['calculation_type'] = calculationType;
        }

        // Add payment terms for this service
        const serviceTermsList = serviceTerms[serviceItem.id] || [];
        if (invoicedSnap?.payment_terms) {
          serviceData.payment_terms = invoicedSnap.payment_terms.map((term, index) => ({
            id: term.id || undefined,
            term_name: term.term_name || `Term ${index + 1}`,
            term_receive_date: term.term_receive_date || '',
            amount_percentage: term.amount_percentage ?? null,
            amount_rs: term.amount_rs ?? null,
          }));
        } else if (!isDigitalMarketingService(serviceItem.service, serviceItem.plan)) {
          serviceData.payment_terms = serviceTermsList.map((term, index) => {
            const td = serviceTermDetails[serviceItem.id]?.[term.id] || {};
            const pctFilled = isTermAmountFilled(td.amount_percentage);
            const rsFilled = isTermAmountFilled(td.amount_rs);
            return {
              id: td.pk || undefined,
              term_name: td.term_name || `Term ${index + 1}`,
              term_receive_date: td.term_receive_date || '',
              amount_percentage: pctFilled ? td.amount_percentage : null,
              amount_rs: !pctFilled && rsFilled ? td.amount_rs : null,
            };
          });
        }

        // Calculate service totals using existing pricing summary
        const { baseAmount, gstAmount, totalAmount } = getServicePricingSummary(serviceItem.id, serviceItem.service, serviceItem.plan);
        serviceData.service_base = baseAmount;
        serviceData.service_gst = gstAmount;
        serviceData.service_total = totalAmount;

        return serviceData;
      });

      // Prepare modules data
      const modules = {};
      Object.keys(selectedModules).forEach(moduleKey => {
        modules[moduleKey] = {
          enabled: selectedModules[moduleKey].checked,
          options: selectedModules[moduleKey].options
        };
      });
      const post_data = {
        config_id: config_id,
        student_strength: parseInt(setup_details.student_strength || overallFetchedData.student_strength, 10) || 0,
        // year,
        pricing_services: services,
        // deletable_service_ids: removedServiceIds.current,
        // deletable_service_term_ids: removedTermIds.current,

        // modules_allowed: modules
      };

      return post_data;
    } else {
      if (!pricingValidate && pricing.current) {
        pricing.current.updateErrors(fieldErrorsTemp);
      }
      if (!additionalValidate && additional.current) {
        additional.current.updateErrors(fieldErrorsTemp);
      }
      // Build human-readable error list for Swal
      const errorLines = [];
      if (!pricingValidate) {
        Object.entries(fieldErrorsTemp).forEach(([k, v]) => errorLines.push(`• ${v} (${k})`));
      }
      Object.entries(serviceFieldErrorsTemp).forEach(([svcId, errs]) => {
        const svc = otherServices.find(s => String(s.id) === String(svcId));
        const svcName = svc?.service?.name || `Service ${svcId}`;
        Object.entries(errs).forEach(([field, msg]) => {
          const label = field.replace(/_\d+$/, '').replace(/_/g, ' ');
          errorLines.push(`• [${svcName}] ${msg || label + ' is required'}`);
        });
      });
      if (!allServiceTermsValid) {
        if (serviceTermErrorLines.length > 0) {
          errorLines.push(...serviceTermErrorLines);
        } else {
          errorLines.push('• Payment terms are invalid (percentages must total 100%)');
        }
      }
      return { __validationFailed: true, errorLines };
    }
  };

  const submit = (redirectAfter = 'list') => {
    // eslint-disable-next-line no-console
    console.log('[SUBMIT] Save order clicked');
    let validate_post_data;
    try {
      validate_post_data = validation();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SUBMIT] validation() threw:', e);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: e?.message || 'Please check all service fields and try again.',
      });
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[SUBMIT] validate_post_data:', validate_post_data);
    if (!validate_post_data || validate_post_data?.__validationFailed) {
      // Auto-expand all service accordions so user can see highlighted errors
      setServiceExpanded(prev => {
        const expanded = { ...prev };
        otherServices.forEach(svc => { expanded[svc.id] = true; });
        return expanded;
      });
      const lines = validate_post_data?.errorLines || [];
      Swal.fire({
        icon: 'warning',
        title: 'Please fix the following errors',
        html: lines.length > 0
          ? `<div style="text-align:left;font-size:14px;">${lines.map(l => `<div style="margin:4px 0;">${l}</div>`).join('')}</div>`
          : 'Some required fields are missing or invalid.',
        confirmButtonText: 'OK',
      });
      return;
    }

    set_submitDisable(true);
    const url = POST_URL.billing_config.api;
    postRequest(url, [validate_post_data], props).then(response => {
      set_submitDisable(false);
      if (response && response.status === 200) {
        const Response = response.data;
        Swal.fire({
          position: 'top-end',
          type: 'success',
          title: Response.Reason || 'Success',
          text: redirectAfter === 'invoice'
            ? 'Order saved. Opening issue invoice…'
            : 'Company Setup saved successfully',
          showConfirmButton: false,
          timer: redirectAfter === 'invoice' ? 800 : 1500
        });
        if (redirectAfter === 'invoice') {
          const savedConfigId = config_id || validate_post_data?.config_id;
          if (savedConfigId) {
            navigate(`/billing/issue-invoice/?config_id=${savedConfigId}`);
          } else {
            navigate('/billing/company-setup/list');
          }
        } else {
          navigate('/billing/company-setup/list');
        }
      }
    }).catch((error) => {
      set_submitDisable(false);
      const msg =
        error?.response?.data?.Reason ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Failed to save order. Please check all fields and try again.';
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: msg,
      });
    });
  };

  const handleBack = async () => {
    const currentState = JSON.stringify({
      setup_details,
      otherServices,
      serviceFieldDetails,
      serviceTerms,
      serviceTermDetails,
      selectedModules
    });

    if (initialDataRef.current && currentState !== initialDataRef.current) {
      const res = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes on this page. Do you want to save them before leaving?',
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#d33',
        confirmButtonText: 'Save and leave',
        denyButtonText: 'Leave without saving',
        cancelButtonText: 'Cancel',
      });
      if (res?.isConfirmed) {
        submit();
      } else if (res?.isDenied) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };


  const formatInr = (n) =>
    `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sectionPaperSx = {
    mb: 2.5,
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: 1,
    borderColor: 'divider',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  };

  return (
    <>
      {loading && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="min(60vh, 480px)"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading order setup…
          </Typography>
        </Box>
      )}
      {!loading && field_details && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 5, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <Paper
            elevation={0}
            sx={{
              mb: 2.5,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.background.paper, 1)} 55%)`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
                  Create order
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 0.75 }}>
                  {overallFetchedData?.company_name && (
                    <Chip
                      label={overallFetchedData.company_name}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {(overallFetchedData?.year_name || overallFetchedData?.year?.name) && (
                    <Chip
                      label={overallFetchedData?.year_name || overallFetchedData?.year?.name}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {otherServices.some((s) => s.hasInvoice)
                    ? 'Invoiced service lines are locked. Add new services below, then save the order.'
                    : 'Add service lines, review amounts, then save the order.'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}
              >
                Back
              </Button>
            </Stack>
          </Paper>
          {(() => {
            const totals = otherServices.reduce((acc, svc) => {
              const { baseAmount, gstAmount, totalAmount } = getServicePricingSummary(svc.id, svc.service, svc.plan);
              acc.base += baseAmount;
              acc.gst += gstAmount;
              acc.total += totalAmount;
              return acc;
            }, { base: 0, gst: 0, total: 0 });
            const yrName = overallFetchedData?.year_name || overallFetchedData?.year?.name;
            const yrCode = overallFetchedData?.year_code || overallFetchedData?.year?.code;
            const yrId = overallFetchedData?.year;
            const academicYearLabel =
              [yrName, yrCode].filter(Boolean).join(' · ') ||
              (yrId != null && yrId !== '' ? `Year #${yrId}` : '');

            const summaryCardSx = {
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            };

            return (
              <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
                  Order summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={summaryCardSx}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Academic year
                      </Typography>
                      <Typography variant="body1" fontWeight={600} noWrap title={academicYearLabel || '—'}>
                        {academicYearLabel || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ ...summaryCardSx, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <TextField
                        label="Student Count"
                        type="number"
                        size="small"
                        fullWidth
                        required
                        value={setup_details.student_strength || ''}
                        inputProps={{ min: 1, step: 1 }}
                        error={Boolean(fieldErrors.student_strength) || (setup_details.student_strength !== undefined && setup_details.student_strength !== '' && Number(setup_details.student_strength) <= 0)}
                        helperText={fieldErrors.student_strength || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setoverallFetchedData(prev => ({ ...prev, student_strength: val }));
                          const newSetup = { ...setup_details, student_strength: val };
                          otherServices.forEach(svc => {
                            if (svc.hasInvoice || svc.idCardConfig || svc.biometricConfig) return;
                            if (isErpServiceLine(svc) && !svc.erpAmountFixed) {
                              newSetup[`item_count_${svc.id}`] = val;
                              return;
                            }
                            if (
                              (svc.plan?.service_type === 'STUDENT_WISE' || isIdCardCatalogService(svc.service, svc.plan))
                              && !isDigitalMarketingService(svc.service, svc.plan)
                            ) {
                              newSetup[`item_count_${svc.id}`] = val;
                            } else if (isDigitalMarketingService(svc.service, svc.plan)) {
                              if (getDigitalMarketingCalculationType(svc.service, svc.plan) === 'studentcount') {
                                newSetup[`item_count_${svc.id}`] = val;
                              }
                            }
                          });
                          set_setup_details(newSetup);
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.75, display: 'block' }}
                        title={overallFetchedData?.client_report_tab ? `Tab: ${overallFetchedData.client_report_tab}` : undefined}
                      >
                        {Number(overallFetchedData?.client_report_student_count) > 0
                          ? `Client report: ${Number(overallFetchedData.client_report_student_count).toLocaleString('en-IN')} students`
                          : 'Client report: no student count'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Box sx={summaryCardSx}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Base amount
                      </Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatInr(totals.base)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Box sx={summaryCardSx}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        GST
                      </Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatInr(totals.gst)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Box
                      sx={{
                        ...summaryCardSx,
                        bgcolor: 'primary.main',
                        borderColor: 'primary.main',
                        color: 'primary.contrastText',
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.85 }} display="block">
                        Grand total
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatInr(totals.total)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            );
          })()}

          {/* Pricing Details Section */}
          {/* <Box className='form-left-heading mv-20'>
            {form_details.pricing_details.label}
          </Box>
          {field_details['pricing'] && (
            <DynamicForm
              fieldDetails={field_details['pricing']}
              updateParent={(name, value, list)=>updateParent(name, value, list, 'pricing')}
              isEditForm={false}
              loading={loading}
              ref={pricing}
            />
          )}
          <Box mt={3} mb={3}>
            <Divider />
          </Box> */}

          {otherServices.some((s) => s.hasInvoice) ? (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              Some services already have issued invoices and cannot be edited or removed. You can still add new service lines and save.
            </Alert>
          ) : null}

          {/* Other Pricing Services Section */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: otherServices.length ? 2 : 0 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {form_details.pricing_services.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Add ERP, ID Cards, Auto/Manual Biometric, or other digital services. Expand a row to edit pricing and terms.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openServiceModal}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                }}
              >
                Add service
              </Button>
            </Stack>

            {otherServices.length === 0 && (
              <Box
                sx={{
                  py: 5,
                  px: 2,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.action.hover, 0.5),
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                  No services yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                  Start by adding a service line. You will pick ERP, ID Cards, or another digital service — templates apply automatically.
                </Typography>
              </Box>
            )}
          </Paper>

          {otherServices.map((serviceItem, index) => {
            const serviceTermsList = serviceTerms[serviceItem.id] || [];
            const serviceTermDetailsForService = serviceTermDetails[serviceItem.id] || {};
            const serviceTermErrorsForService = serviceTermErrors[serviceItem.id] || {};
            const serviceTermValidationError = serviceTermValidationErrors[serviceItem.id] || '';

            return (
              <Accordion
                key={serviceItem.id}
                expanded={!!serviceExpanded[serviceItem.id]}
                onChange={() => setServiceExpanded((prev) => ({ ...prev, [serviceItem.id]: !prev[serviceItem.id] }))}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                    '&.Mui-expanded': { minHeight: 56, borderBottom: 1, borderColor: 'divider' },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{ fontWeight: 700, minWidth: 36 }}
                      />
                      <Chip
                        label={serviceItem.service?.name || ''}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      {serviceItem.hasInvoice ? (
                        <Chip label="Invoiced" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                      ) : null}
                      {isErpServiceLine(serviceItem) ? (
                        <Chip
                          label={serviceItem.erpAmountFixed ? 'Fixed amount' : 'Per student'}
                          size="small"
                          variant="outlined"
                          color={serviceItem.erpAmountFixed ? 'default' : 'info'}
                          sx={{ fontWeight: 600 }}
                        />
                      ) : null}
                      {isIdCardGstEligibleLine(serviceItem) ? (
                        <Chip
                          label={idCardAddGst[serviceItem.id] ? 'With GST' : 'Without GST'}
                          size="small"
                          variant="outlined"
                          color={idCardAddGst[serviceItem.id] ? 'info' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      ) : null}
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Template: {serviceItem.plan?.name || '—'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      {(() => {
                        const { baseAmount, gstAmount, totalAmount, calculationType } = getServicePricingSummary(serviceItem.id, serviceItem.service, serviceItem.plan);
                        const isDigital = isDigitalMarketingService(serviceItem.service, serviceItem.plan);
                        const unitPrice = parseFloat(setup_details[`service_amount_${serviceItem.id}`]) || 0;
                        const unitLabel = calculationType === 'studentcount' ? 'Per student' : calculationType === 'yearwise' ? 'Yearly' : 'Monthly';
                        if (!!serviceExpanded[serviceItem.id]) return null;
                        if (!totalAmount) return null;
                        return (
                          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mr: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {isDigital ? `${unitLabel}` : 'Base'}:{' '}
                              <Box component="span" fontWeight={600} color="text.primary">
                                {formatInr(isDigital ? unitPrice : baseAmount)}
                              </Box>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              GST:{' '}
                              <Box component="span" fontWeight={600} color="text.primary">
                                {formatInr(gstAmount)}
                              </Box>
                            </Typography>
                            <Chip
                              label={formatInr(totalAmount)}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                            />
                          </Stack>
                        );
                      })()}
                      {!serviceItem.hasInvoice ? (
                        <IconButton
                          color="error"
                          onClick={(e) => { e.stopPropagation(); removeService(serviceItem.id); }}
                          size="small"
                          aria-label="Remove service"
                          sx={{ ml: { xs: 0, sm: 1 } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2.5, pt: 2, bgcolor: 'background.paper' }}>
                  {serviceItem.idCardConfig ? (
                    <IdCardServiceConfigPanel
                      config={serviceItem.idCardConfig}
                      onChange={(nextConfig) => handleIdCardConfigChange(serviceItem.id, nextConfig)}
                      disabled={Boolean(serviceItem.hasInvoice)}
                    />
                  ) : null}
                  {serviceItem.biometricConfig ? (
                    <BiometricServiceConfigPanel
                      config={serviceItem.biometricConfig}
                      onChange={(nextConfig) => handleBiometricConfigChange(serviceItem.id, nextConfig)}
                      disabled={Boolean(serviceItem.hasInvoice)}
                    />
                  ) : null}
                  {serviceFieldDetails[serviceItem.id] && (
                    <DynamicForm
                      fieldDetails={serviceFieldDetails[serviceItem.id]}
                      updateParent={(name, value, list) => updateParent(name, value, list, serviceItem)}
                      isEditForm={false}
                      loading={loading}
                      ref={el => serviceRefs.current[serviceItem.id] = el}
                      externalErrors={serviceFieldErrors[serviceItem.id] || {}}
                    />
                  )}
                  {isErpServiceLine(serviceItem) ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.action.hover, 0.35),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          ERP billing type
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {serviceItem.erpAmountFixed
                            ? 'Enter the final taxable amount, then GST is added on top.'
                            : 'Enter per-student amount × student count = taxable base, then GST is added.'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            checked={Boolean(serviceItem.erpAmountFixed)}
                            disabled={Boolean(serviceItem.hasInvoice)}
                            onChange={(e) => handleErpAmountFixedChange(serviceItem.id, e.target.checked)}
                            color="primary"
                          />
                        )}
                        label={
                          <Typography variant="body2" fontWeight={600}>
                            {serviceItem.erpAmountFixed ? 'Fixed amount' : 'Per student'}
                          </Typography>
                        }
                      />
                    </Box>
                  ) : null}
                  {isIdCardGstEligibleLine(serviceItem) ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.action.hover, 0.35),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Add GST
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {idCardAddGst[serviceItem.id]
                            ? (serviceItem.idCardConfig
                              ? 'GST is added on the ID card grand total.'
                              : 'GST is added on students × per-ID amount.')
                            : 'Order this ID card line without GST. Turn on to add GST.'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        {idCardAddGst[serviceItem.id] ? (
                          <TextField
                            label="GST in %"
                            type="number"
                            size="small"
                            value={setup_details[`service_gst_percentage_${serviceItem.id}`] ?? ''}
                            onChange={(e) => handleIdCardGstPercentChange(serviceItem.id, e.target.value)}
                            disabled={Boolean(serviceItem.hasInvoice)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            sx={{ width: 120 }}
                          />
                        ) : null}
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={(
                            <Switch
                              checked={Boolean(idCardAddGst[serviceItem.id])}
                              disabled={Boolean(serviceItem.hasInvoice)}
                              onChange={(e) => handleIdCardAddGstChange(serviceItem.id, e.target.checked)}
                              color="primary"
                            />
                          )}
                          label={
                            <Typography variant="body2" fontWeight={600}>
                              {idCardAddGst[serviceItem.id] ? 'GST on' : 'GST off'}
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  ) : null}
                  {serviceFieldErrors[serviceItem.id]?.id_card_config ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {serviceFieldErrors[serviceItem.id].id_card_config}
                    </Alert>
                  ) : null}
                  {serviceFieldErrors[serviceItem.id]?.biometric_config ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {serviceFieldErrors[serviceItem.id].biometric_config}
                    </Alert>
                  ) : null}

                  {(() => {
                    const { baseAmount, gstAmount, totalAmount, gstPercent, monthCount, calculationType, itemCount } = getServicePricingSummary(serviceItem.id, serviceItem.service, serviceItem.plan);
                    const isDigital = isDigitalMarketingService(serviceItem.service, serviceItem.plan);
                    const isErp = isErpServiceLine(serviceItem);
                    const unitPrice = parseFloat(setup_details[`service_amount_${serviceItem.id}`]) || 0;
                    const from = setup_details[`order_date_${serviceItem.id}`] || '';
                    const to = setup_details[`service_end_date_${serviceItem.id}`] || '';
                    const hasValidDigitalQuantity = calculationType === 'monthwise' ? (!!from && !!to && monthCount > 0) : itemCount > 0;
                    const unitLabel = calculationType === 'studentcount' ? 'Per student price' : calculationType === 'yearwise' ? 'Yearly price' : 'Monthly price';
                    const quantityLabel =
                      calculationType === 'studentcount'
                        ? `${itemCount} student(s)`
                        : calculationType === 'yearwise'
                          ? `${itemCount} year(s)`
                          : `${monthCount} month(s)`;
                    const erpUnitLabel = serviceItem.erpAmountFixed ? 'Fixed amount' : 'Per student';
                    const miniCard = (label, value, emphasize) => (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: emphasize ? 'primary.main' : 'background.paper',
                          color: emphasize ? 'primary.contrastText' : 'text.primary',
                          border: 1,
                          borderColor: emphasize ? 'primary.main' : 'divider',
                          flex: '1 1 140px',
                          minWidth: 140,
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: emphasize ? 0.85 : 1 }} color={emphasize ? 'inherit' : 'text.secondary'} display="block">
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={emphasize ? 700 : 600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {value}
                        </Typography>
                      </Box>
                    );
                    return (
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: (theme) => alpha(theme.palette.action.hover, 0.4),
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700} color="text.primary" gutterBottom>
                          {isDigital ? 'Digital billing summary' : 'Pricing summary'}
                        </Typography>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          {isDigital ? (
                            <>
                              {miniCard(unitLabel, formatInr(unitPrice), false)}
                              {miniCard('From', from || '—', false)}
                              {miniCard('To', to || '—', false)}
                              {miniCard('Quantity', quantityLabel, false)}
                              {miniCard('Amount', formatInr(hasValidDigitalQuantity ? baseAmount : 0), false)}
                              {miniCard(
                                `GST (${gstPercent || 0}%)`,
                                formatInr(hasValidDigitalQuantity ? gstAmount : 0),
                                false,
                              )}
                              {miniCard(
                                'Total',
                                formatInr(hasValidDigitalQuantity ? totalAmount : 0),
                                true,
                              )}
                            </>
                          ) : (
                            <>
                              {miniCard(
                                isErp ? erpUnitLabel : 'Unit price',
                                isErp && !serviceItem.erpAmountFixed
                                  ? `${formatInr(unitPrice)} × ${itemCount}`
                                  : `${formatInr(unitPrice)}${!isErp && itemCount > 1 ? ` × ${itemCount}` : ''}`,
                                false,
                              )}
                              {miniCard('Amount', formatInr(baseAmount), false)}
                              {miniCard(`GST (${gstPercent || 0}%)`, formatInr(gstAmount), false)}
                              {miniCard('Total', formatInr(totalAmount), true)}
                            </>
                          )}
                        </Stack>
                        {isDigital && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            Calculation: {formatInr(unitPrice)} × {quantityLabel} = {formatInr(baseAmount)}
                            {gstPercent > 0 ? ` + GST ${gstPercent}% = ${formatInr(totalAmount)}` : ''}
                          </Typography>
                        )}
                        {isErp && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            {serviceItem.erpAmountFixed
                              ? `Fixed amount ${formatInr(unitPrice)} + GST ${gstPercent || 0}% (${formatInr(gstAmount)}) = ${formatInr(totalAmount)}`
                              : `${formatInr(unitPrice)} × ${itemCount} students = ${formatInr(baseAmount)} + GST ${gstPercent || 0}% (${formatInr(gstAmount)}) = ${formatInr(totalAmount)}`}
                          </Typography>
                        )}
                        {serviceItem.idCardConfig && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            {gstPercent > 0
                              ? `ID card total ${formatInr(baseAmount)} + GST ${gstPercent}% (${formatInr(gstAmount)}) = ${formatInr(totalAmount)}`
                              : `ID card total ${formatInr(baseAmount)}`}
                          </Typography>
                        )}
                        {isIdCardCatalogService(serviceItem.service, serviceItem.plan) && !serviceItem.idCardConfig && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            {gstPercent > 0
                              ? `${formatInr(unitPrice)} × ${itemCount} students = ${formatInr(baseAmount)} + GST ${gstPercent}% (${formatInr(gstAmount)}) = ${formatInr(totalAmount)}`
                              : `${formatInr(unitPrice)} × ${itemCount} students = ${formatInr(baseAmount)}`}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })()}

                  {!isDigitalMarketingService(serviceItem.service, serviceItem.plan) && ['termly', 'monthly', 'one_time'].includes(serviceItem.plan?.billing_cycle) && (
                    <Box mt={3} pt={2.5} borderTop={1} borderColor="divider">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                            Payment terms
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {serviceItem.service?.name || 'Service'}
                          </Typography>
                          {(() => {
                            const pctSum = getTermPercentageSum(serviceItem.id);
                            const hasPct = (serviceTermsList || []).some((term) => (
                              isTermAmountFilled(serviceTermDetailsForService[term.id]?.amount_percentage)
                            ));
                            if (!hasPct) return null;
                            const isHundred = Math.abs(pctSum - 100) <= 0.01;
                            return (
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 0.25, fontWeight: 700 }}
                                color={isHundred ? 'success.main' : 'error.main'}
                              >
                                {isHundred
                                  ? `Total ${pctSum.toFixed(2)}%`
                                  : `Total ${pctSum.toFixed(2)}% — must equal 100%`}
                              </Typography>
                            );
                          })()}
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={(e) => { e.stopPropagation(); addServiceTerm(serviceItem.id); }}
                          disabled={Boolean(serviceItem.hasInvoice)}
                          sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                          Add term
                        </Button>
                      </Box>

                      {serviceTermValidationError && (
                        <Box mb={2}>
                          <Alert severity="error">{serviceTermValidationError}</Alert>
                        </Box>
                      )}

                      {serviceTermsList.length === 0 ? (
                        <Box
                          p={2.5}
                          textAlign="center"
                          sx={{
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No payment terms yet. Add a term to schedule receivables.
                          </Typography>
                        </Box>
                      ) : (
                        serviceTermsList.map((term, termIndex) => (
                          <Paper
                            key={term.id}
                            elevation={0}
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              border: 1,
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Chip label={`Term ${termIndex + 1}`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                              {serviceTermsList.length > 1 && !serviceItem.hasInvoice && (
                                <IconButton
                                  color="error"
                                  onClick={(e) => { e.stopPropagation(); removeServiceTerm(serviceItem.id, term.id); }}
                                  size="small"
                                  aria-label="Remove term"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                  label="Term Name"
                                  variant="outlined"
                                  fullWidth
                                  required
                                  size="small"
                                  value={serviceTermDetailsForService[term.id]?.term_name || ''}
                                  onChange={(e) => handleServiceTermFieldChange(serviceItem.id, term.id, 'term_name', e.target.value)}
                                  disabled={Boolean(serviceItem.hasInvoice)}
                                  error={!!serviceTermErrorsForService[`${term.id}_term_name`]}
                                  helperText={serviceTermErrorsForService[`${term.id}_term_name`] || ''}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                  label="Term Amount Receive Date"
                                  type="date"
                                  variant="outlined"
                                  fullWidth
                                  required
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  value={serviceTermDetailsForService[term.id]?.term_receive_date || ''}
                                  onChange={(e) => handleServiceTermFieldChange(serviceItem.id, term.id, 'term_receive_date', e.target.value)}
                                  disabled={Boolean(serviceItem.hasInvoice)}
                                  error={!!serviceTermErrorsForService[`${term.id}_term_receive_date`]}
                                  helperText={serviceTermErrorsForService[`${term.id}_term_receive_date`] || ''}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                  label="Amount in %"
                                  variant="outlined"
                                  fullWidth
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0, max: 100 }}
                                  value={serviceTermDetailsForService[term.id]?.amount_percentage || ''}
                                  onChange={(e) => handleServiceTermFieldChange(serviceItem.id, term.id, 'amount_percentage', e.target.value)}
                                  disabled={Boolean(serviceItem.hasInvoice) || isTermAmountFilled(serviceTermDetailsForService[term.id]?.amount_rs)}
                                  error={!!serviceTermErrorsForService[`${term.id}_amount`]}
                                  helperText={
                                    isTermAmountFilled(serviceTermDetailsForService[term.id]?.amount_rs)
                                      ? 'Disabled'
                                      : (serviceTermErrorsForService[`${term.id}_amount`] || '')
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                  label="Amount in Rs"
                                  variant="outlined"
                                  fullWidth
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0, max: 1000000 }}
                                  value={(() => {
                                    const td = serviceTermDetailsForService[term.id] || {};
                                    if (isTermAmountFilled(td.amount_percentage)) {
                                      const { baseAmount } = getServicePricingSummary(serviceItem.id, serviceItem.service, serviceItem.plan);
                                      const pct = parseFloat(td.amount_percentage) || 0;
                                      return (baseAmount * (pct / 100)).toFixed(2);
                                    }
                                    return td.amount_rs || '';
                                  })()}
                                  onChange={(e) => handleServiceTermFieldChange(serviceItem.id, term.id, 'amount_rs', e.target.value)}
                                  disabled={Boolean(serviceItem.hasInvoice) || isTermAmountFilled(serviceTermDetailsForService[term.id]?.amount_percentage)}
                                  error={!!(serviceTermErrorsForService[`${term.id}_amount`] || serviceTermErrorsForService[`${term.id}_amount_rs`])}
                                  helperText={
                                    isTermAmountFilled(serviceTermDetailsForService[term.id]?.amount_percentage)
                                      ? 'Disabled'
                                      : (serviceTermErrorsForService[`${term.id}_amount_rs`] || serviceTermErrorsForService[`${term.id}_amount`] || '')
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={2}>
                                <Box
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                                    border: 1,
                                    borderColor: (theme) => alpha(theme.palette.success.main, 0.25),
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  {(() => {
                                    let termBaseAmount = 0;
                                    let termGstAmount = 0;
                                    let termTotalAmount = 0;
                                    const { baseAmount, totalAmount, gstPercent } = getServicePricingSummary(serviceItem.id, serviceItem.service, serviceItem.plan);
                                    if (totalAmount === 0 && baseAmount === 0) {
                                      return (
                                        <>
                                          <Typography variant="caption" color="text.secondary">Amount: {formatInr(0)}</Typography>
                                          <Typography variant="caption" color="text.secondary">GST (0%): {formatInr(0)}</Typography>
                                          <Typography variant="caption" color="primary" fontWeight={700}>Total: {formatInr(0)}</Typography>
                                        </>
                                      );
                                    }
                                    if (serviceTermDetailsForService[term.id]?.amount_percentage) {
                                      const percent = parseFloat(serviceTermDetailsForService[term.id].amount_percentage) / 100;
                                      termBaseAmount = baseAmount * percent;
                                      termGstAmount = gstPercent > 0 ? (termBaseAmount * (gstPercent / 100)) : 0;
                                      termTotalAmount = termBaseAmount + termGstAmount;
                                    } else if (serviceTermDetailsForService[term.id]?.amount_rs) {
                                      termBaseAmount = parseFloat(serviceTermDetailsForService[term.id]?.amount_rs || 0);
                                      termGstAmount = gstPercent > 0 ? termBaseAmount * (gstPercent / 100) : 0
                                      termTotalAmount = termBaseAmount + termGstAmount;
                                    }
                                    return (
                                      <>
                                        <Typography variant="caption" color="text.secondary">
                                          Amount: {formatInr(termBaseAmount)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          GST ({gstPercent || 0}%): {formatInr(termGstAmount)}
                                        </Typography>
                                        <Typography variant="caption" color="primary" fontWeight={700}>
                                          Total: {formatInr(termTotalAmount)}
                                        </Typography>
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Service Selection Modal */}
          <ServiceSelectionModal
            open={serviceModalOpen}
            onClose={closeServiceModal}
            onSelect={handleServicePlanSelect}
            props={props}
          />

          <IdCardServiceConfigModal
            open={idCardConfigModalOpen}
            onClose={() => {
              setIdCardConfigModalOpen(false);
              setPendingIdCardSelection(null);
            }}
            onConfirm={handleIdCardConfigConfirm}
          />

          {/* <Box mt={3} mb={3}>
            <Divider />
          </Box> */}

          {/* Modules Section */}
          {/* <Box className='form-left-heading mv-20'>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              Module Allowed to Use
            </Typography>
          </Box> */}

          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Modules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which modules this company can use.
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(ModulesConfig).map((moduleKey) => (
                <Grid item xs={12} sm={6} md={4} key={moduleKey}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: 1,
                      borderColor: 'divider',
                      '&:before': { display: 'none' },
                      overflow: 'hidden',
                      bgcolor: selectedModules[moduleKey]?.checked
                        ? (theme) => alpha(theme.palette.primary.main, 0.04)
                        : 'background.paper',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 1.5, bgcolor: 'transparent' }}
                    >
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        control={
                          <Checkbox
                            checked={selectedModules[moduleKey]?.checked || false}
                            indeterminate={selectedModules[moduleKey]?.indeterminate || false}
                            onChange={() => handleModuleChange(moduleKey)}
                            color="primary"
                          />
                        }
                        label={<Typography fontWeight={600}>{ModulesConfig[moduleKey].label}</Typography>}
                      />
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Box display="flex" flexDirection="column" ml={1}>
                        {ModulesConfig[moduleKey].options.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            control={
                              <Checkbox
                                checked={selectedModules[moduleKey]?.options[option.id] || false}
                                onChange={() => handleOptionChange(moduleKey, option.id)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={option.name}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              bottom: 16,
              zIndex: 2,
              mt: 3,
              p: 2,
              borderRadius: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {otherServices.length} service{otherServices.length === 1 ? '' : 's'} selected
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                disabled={submitDisable}
                onClick={() => submit('invoice')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                }}
              >
                Save and Invoice
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={submitDisable}
                onClick={() => submit()}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                }}
              >
                Save order
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </>
  );
}

export default CompanyOrder;
